//managebooks.tsx
import React, { MouseEvent, useEffect, useState, useCallback, useMemo, useDeferredValue, useTransition, useLayoutEffect, useRef } from 'react';
import { Spin, Modal, Button, Input, Upload, Card, Select, message, Dropdown, Tooltip, Empty, Space, Alert, Popover } from 'antd';
import type { TourProps, UploadProps } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { 
  UploadOutlined, 
  QuestionCircleOutlined,
  TranslationOutlined, 
  DeleteOutlined, 
  ClearOutlined, 
  BookOutlined,
  ReadOutlined,
  UserOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  FileWordOutlined,
  FilePdfOutlined,
  ReloadOutlined,  
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ExclamationCircleFilled,
  FormOutlined,
  MoreOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { User } from "firebase/auth";
import axios, { AxiosError } from 'axios';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from "firebase/auth";
import { FixedSizeGrid as VirtualGrid, GridChildComponentProps } from 'react-window';

import BookUploadForm, { type NewBookPreflightResult } from './BookUploadForm';
import { getAllLanguages as getAllConfiguredLanguages } from './LanguageConfig';
import {
  canonicalizeLanguageKey,
  canonicalizeLanguageName,
  canonicalizeTranslationProgressMap,
  getLanguageCountryCode,
} from './utils/languageIdentity';

import { WORD_COUNT_VERSION } from './utils/wordcount';
import { getAuthHeaders } from './utils/authHeaders';
import { requestWithRetry } from './utils/requestWithRetry';
import { getTargetLanguageFromPair } from './utils/languagePairs';
import {
  bookMatchesLanguageStatus,
  LibraryProgressState,
  LibraryTranslationStatus,
} from './utils/manageBooksLanguageStatus';
import { SeriesSummary, TranslationProgressEntry } from './types';
import LoadingCard from './components/LoadingCard';
import AppPageHeader from './components/AppPageHeader';
import { isTutorialSandboxModeEnabled, isTutorialUiSuppressionRequested } from './utils/tutorialMode';
import { resolveFrontendApiBase } from './utils/apiBase';
import { buildTutorialManageBooksFixture, useTutorialSandbox } from './tutorials/TutorialSandboxProvider';
import AppTour, { AppTourVideoLink, renderAppTourPanel } from './tours/AppTour';
import { tutorialVideoLinks } from './tutorialVideoLinks';
import './ManageBooks.css';

const { Option } = Select;

export { resolveFrontendApiBase as resolveManageBooksApiBase } from './utils/apiBase';

const API_BASE = resolveFrontendApiBase();

const WordIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="#2b5797">
    <path d="M14,2H6C4.89,2 4,2.89 4,4V20C4,21.11 4.89,22 6,22H18C19.11,22 20,21.11 20,20V8L14,2M18,20H6V4H13V9H18V20M9.5,11.5L7.5,16H9.5L10.25,14H12.75L13.5,16H15.5L13.5,11.5C13.5,11.5 12.5,11.5 11.5,11.5C10.5,11.5 9.5,11.5 9.5,11.5M11.5,12.5H12.5L13,13.5H11L11.5,12.5Z" />
  </svg>
);

// Modern styled components
const Container = styled.div`
  background-color: var(--bg-app);
  color: var(--text-main);
  padding: 2em;
  min-height: calc(100vh - 64px);
  
  @media (max-width: 768px) {
    padding: 1em;
  }
`;

const StyledCard = styled(Card)`
  background-color: var(--surface-base);
  border-radius: 12px;
  box-shadow: var(--shadow-elevated);
  border: 1px solid var(--border-subtle);
  padding: 2em;
  color: var(--text-main);
  
  @media (max-width: 768px) {
    padding: 1em;
  }
`;

const BookItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  width: 150px;
  margin-bottom: 4px;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-3px);
  }
`;

const AuthorBooksContainer = styled.div`
  margin-top: 14px;
  background-color: var(--surface-base);
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
  overflow: hidden;

  /* Performance */
  content-visibility: auto;
  contain-intrinsic-size: 800px; /* reserve space to avoid layout jumps */
`;

const AuthorHeader = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-main);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  background: var(--surface-muted);
  border-bottom: 1px solid var(--border-subtle);
  
  &:hover {
    color: var(--accent-primary);
    background: color-mix(in srgb, var(--surface-info) 55%, var(--surface-muted));
  }
  
  svg {
    color: var(--accent-primary);
  }
`;

// Series header with subtle line separator
const SeriesHeader = styled.div`
  font-size: 0.9rem;
  margin: 0;
  padding: 12px 16px 12px 32px;
  color: var(--text-main);
  font-weight: 650;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-top: 1px solid var(--border-subtle);
  background-color: var(--surface-base);
  
  &:hover {
    color: var(--accent-primary);
    background: var(--surface-muted);
  }
  
  svg {
    color: var(--accent-primary);
    font-size: 1rem;
  }
`;


const BookCover = styled.div<{ coverUrl?: string }>`
  position: relative;
  border-radius: 8px;
  height: 220px;
  width: 140px;
  margin-bottom: 0;
  border: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-elevated);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  background: var(--surface-muted);

  &:hover {
    transform: scale(1.015);
    border-color: color-mix(in srgb, var(--accent-primary) 45%, var(--border-subtle));
  }

  &:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 3px;
  }
`;

const CoverSpinner = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
  z-index: 2;
  pointer-events: all;
`;

const BookCoverImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BookTitle = styled.div<{ coverUrl?: string }>`
  display: ${props => (props.coverUrl ? 'none' : 'flex')};
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--surface-muted) 88%, var(--surface-base));
  color: var(--text-main);
  height: 100%;
  text-align: center;
  padding: 18px 12px;
  font-weight: 500;
`;

const BookCardMenu = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;

  .ant-btn {
    width: 30px;
    min-width: 30px;
    height: 30px;
    padding: 0;
    border-radius: 999px;
    border-color: color-mix(in srgb, var(--border-subtle) 70%, transparent);
    background: color-mix(in srgb, var(--surface-base) 92%, transparent);
    color: var(--text-main);
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.16);
  }
`;

const BookGridWrapper = styled.div`
  margin-top: 12px;
  padding: 0 16px 18px 48px;

  @media (max-width: 576px) {
    padding: 0 16px 0 40px;
  }
`;

const BookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  grid-gap: 24px;
  
  @media (max-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    grid-gap: 16px;
  }
`;

const ModalTitle = styled.h3`
  text-align: center;
  margin: 0;
  color: var(--text-main);
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
`;

const ModalChecklist = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  text-align: center;
`;

const ModalBody = styled.div`
  position: relative;
`;

const ModalSpinnerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--text-main);
  font-weight: 500;
  z-index: 1;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  flex-wrap: wrap;
  gap: 16px;
  background-color: var(--surface-muted);
  padding: 12px;
  border-radius: 10px;
  border: 1px solid var(--border-subtle);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    width: 100%;
    
    .ant-select, .ant-input-search {
      width: 100% !important;
    }
  }
`;

const FilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  text-align: center;
`;

const EmptyStateText = styled.p`
  color: var(--text-secondary);
  font-size: 1.1rem;
  margin: 16px 0;
`;

const BookText = styled.div`
  font-size: 0.9rem;
  margin-top: 5px;
  margin-bottom: 6px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 140px;
  color: var(--text-main);
  font-weight: 500;
`;

const SeriesInfo = styled.span`
  display: inline-block;
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 2px;
`;

const BlockingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10;
  pointer-events: all;
  backdrop-filter: blur(1px);
  padding-top: 32px;
`;

const BlockingSpinnerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  font-weight: 600;
  color: var(--text-main);
  background: var(--surface-base);
  border: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-elevated);
  border-radius: 12px;
  padding: 16px 20px;
  min-width: 240px;

  .ant-spin {
    color: inherit;
  }

  .ant-spin-dot-item {
    background-color: currentColor;
  }
`;

const BookCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  background-color: var(--surface-muted);
  color: var(--text-secondary);
  padding: 3px 8px;
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
  font-weight: 700;
`;

const NO_SERIES_LABEL = 'No Series';

export const normalizeLibrarySeriesName = (
  value: string | null | undefined,
  fallback = NO_SERIES_LABEL
): string => {
  const trimmed = (value || '').replace(/\s+/g, ' ').trim();
  return trimmed.length > 0 ? trimmed : fallback;
};
const CARD_WIDTH = 150;
const CARD_GAP = 24;
const CARD_HEIGHT = 408;
const VIRTUALIZATION_THRESHOLD = 60;
const MAX_VIRTUALIZED_HEIGHT = 720;
const DEFAULT_MANAGE_BOOKS_GROUP_BY_MODE = 'author_series' as const;
const PROGRESS_REFRESH_POLL_INTERVAL_MS = 4000;
const PROGRESS_REFRESH_MAX_ATTEMPTS = 5;

const CollapseIcon = styled.span`
  margin-right: 10px;
  transition: transform 0.3s ease;
  display: inline-flex;
  font-size: 14px;
`;

const SeriesContent = styled.div`
  padding: 0 0 20px 0;
  background-color: var(--surface-base);

  /* Performance */
  content-visibility: auto;
  contain-intrinsic-size: 500px;
`;

const FileTypeBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: var(--surface-popover);
  border-radius: 6px;
  padding: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-main);
`;

const legacyLanguages = ["Spanish"]; // Languages no longer available for new translations but may exist in old data


const ProgressBadge = styled.div<{ $percentage: number }>`
  position: absolute;
  bottom: -4px;
  right: -4px;
  background-color: ${props => {
    if (props.$percentage >= 100) return 'var(--text-success)';
    if (props.$percentage >= 50) return 'var(--text-warning)';
    return 'var(--text-danger)';
  }};
  color: var(--surface-base);
  font-size: 9px;
  font-weight: 600;
  padding: 1px 3px;
  border-radius: 4px;
  min-width: 20px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  z-index: 2;
`;

const FlagWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const TranslationSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background-color: var(--surface-muted);
  border-radius: 2px;
  overflow: hidden;
  max-width: 80px;
`;

const ProgressFill = styled.div<{ $percentage: number }>`
  height: 100%;
  background-color: ${props => {
    if (props.$percentage >= 100) return 'var(--text-success)';
    if (props.$percentage >= 50) return 'var(--text-warning)';
    return 'var(--text-danger)';
  }};
  width: ${props => props.$percentage}%;
  transition: width 0.3s ease;
`;

const FlagIcon = styled.span<{ 
  $isAvailable?: boolean;
  $status?: 'complete' | 'in_progress' | 'not_started';
  $percentage?: number;
}>`
  display: inline-flex;
  width: 26px;
  height: 19px;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;
  opacity: ${props => {
    switch(props.$status) {
      case 'complete': return 1;
      case 'in_progress': return 0.9;
      default: return 0.3;
    }
  }};
  
  filter: ${props => {
    switch(props.$status) {
      case 'complete': return 'none';
      case 'in_progress': return 'none';
      default: return 'grayscale(100%)';
    }
  }};
  
  &:hover {
    transform: ${props => (props.$isAvailable || props.$status === 'in_progress') ? 'scale(1.15)' : 'none'};
    box-shadow: ${props => (props.$isAvailable || props.$status === 'in_progress') ? '0 3px 8px rgba(0, 0, 0, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.12)'};
    z-index: ${props => (props.$isAvailable || props.$status === 'in_progress') ? 10 : 'auto'};
  }
  
  // Progress border based on percentage
  box-shadow: ${props => {
    if (props.$status === 'complete') {
      return '0 0 0 2px var(--text-success)';
    }
    if (props.$status === 'in_progress') {
      const percentage = props.$percentage || 0;
      let color = 'var(--text-danger)';
      if (percentage >= 100) color = 'var(--text-success)';
      else if (percentage >= 50) color = 'var(--text-warning)';
      
      return `0 0 0 2px ${color}`;
    }
    return '0 1px 3px rgba(0, 0, 0, 0.12)';
  }};
  
  // Inner border for better definition
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    pointer-events: none;
  }
  

  
  // Flag images
  &[data-country="NL"] { background-image: url('https://flagcdn.com/w40/nl.png'); }
  &[data-country="US"] { background-image: url('https://flagcdn.com/w40/us.png'); }
  &[data-country="GB"] { background-image: url('https://flagcdn.com/w40/gb.png'); }
  &[data-country="FR"] { background-image: url('https://flagcdn.com/w40/fr.png'); }
  &[data-country="DE"] { background-image: url('https://flagcdn.com/w40/de.png'); }
  &[data-country="IT"] { background-image: url('https://flagcdn.com/w40/it.png'); }
  &[data-country="PT"] { background-image: url('https://flagcdn.com/w40/pt.png'); }
  &[data-country="BR"] { background-image: url('https://flagcdn.com/w40/br.png'); }
  &[data-country="ES"] { background-image: url('https://flagcdn.com/w40/es.png'); }
  &[data-country="MX"] { background-image: url('https://flagcdn.com/w40/mx.png'); }
  &[data-country="JP"] { background-image: url('https://flagcdn.com/w40/jp.png'); }
  &[data-country="KR"] { background-image: url('https://flagcdn.com/w40/kr.png'); }
  &[data-country="TW"] { background-image: url('https://flagcdn.com/w40/tw.png'); }
  &[data-country="CN"] { background-image: url('https://flagcdn.com/w40/cn.png'); }
  &[data-country="RU"] { background-image: url('https://flagcdn.com/w40/ru.png'); }
  &[data-country="TH"] { background-image: url('https://flagcdn.com/w40/th.png'); }
  &[data-country="IN"] { background-image: url('https://flagcdn.com/w40/in.png'); }
  &[data-country="AD"] { background-image: url('https://flagcdn.com/w40/ad.png'); }
  &[data-country="AR"] { background-image: url('https://flagcdn.com/w40/ar.png'); }  
  &[data-country="PL"] { background-image: url('https://flagcdn.com/w40/pl.png'); }
  &[data-country="UA"] { background-image: url('https://flagcdn.com/w40/ua.png'); }
  &[data-country="SE"] { background-image: url('https://flagcdn.com/w40/se.png'); }  
  &[data-country="NO"] { background-image: url('https://flagcdn.com/w40/no.png'); }
  &[data-country="RS"] { background-image: url('https://flagcdn.com/w40/rs.png'); }
  &[data-country="ZA"] { background-image: url('https://flagcdn.com/w40/za.png'); }
  &[data-country="TR"] { background-image: url('https://flagcdn.com/w40/tr.png'); }
  &[data-country="CZ"] { background-image: url('https://flagcdn.com/w40/cz.png'); }
  &[data-country="DK"] { background-image: url('https://flagcdn.com/w40/dk.png'); }
  &[data-country="BA"] { background-image: url('https://flagcdn.com/w40/ba.png'); }
  &[data-country="HR"] { background-image: url('https://flagcdn.com/w40/hr.png'); }
  &[data-country="FI"] { background-image: url('https://flagcdn.com/w40/fi.png'); }
  &[data-country="IS"] { background-image: url('https://flagcdn.com/w40/is.png'); }
  &[data-country="BG"] { background-image: url('https://flagcdn.com/w40/bg.png'); }

  /* Hebrew: use a neutral, non-national representation */
  &[data-country="HE"] {
    background-image: none;
    background-color: var(--surface-muted); /* subtle neutral background */
    color: var(--text-main);
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
    font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
  }
`;

const FlagList = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px 4px;  // Changed from just '4px' to '8px 4px' (vertical horizontal)
  margin-top: 8px;
  width: 154px;  
  justify-items: center;
  padding: 6px;  // Increased from 4px
  background-color: var(--surface-muted);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
`;

const FlagEmoji = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  width: 26px;
  height: 19px;
  background-color: var(--surface-muted);
  border-radius: 4px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: scale(1.15);
    background-color: #e3f2ff;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
    z-index: 10;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    pointer-events: none;
  }
`;


// Interface for books
interface IBook {
  bookguid: string;
  title: string;
  author: string;
  series: string;
  seriesId?: string | null;
  seriesBookNumber?: number;
  coverUrl?: string;
  uploadType?: string;
  language?: string;
  word_count_version?: number;
  manual_completed_languages?: string[];
  translation_pairs?: string[];
  translated_titles?: Record<string, string>;
  has_blocking_lock?: boolean;
  active_blocking_locks?: { id: string; status: string; type?: string; name?: string }[];
  needs_translation_refresh?: boolean;
  translation_progress?: Record<string, TranslationProgressEntry>;
  progress_state?: LibraryProgressState;
  flagStatus?: 'ready' | 'loading' | 'error';
  detailsLoaded?: boolean;
  updatedAt?: string;
  updated_at?: string;
  created_at?: string;
}


interface BookRecord extends IBook {
  _searchTitle: string;
  _searchAuthor: string;
  _searchSeries: string;
}


interface Series {
  name: string;
  books: BookRecord[];
}

interface AuthorSeriesGroup {
  author: string;
  series: Series[];
}

type ReplacePreviewRow = {
  temp_id: string;
  title: string;
  word_count: number;
  snippet: string;
  chapter_number: number;
};

type SeriesNotification = (text: string) => void;

type NavigationHandlerDeps = {
  navigate: (path: string) => void;
  notifyStandaloneSeries: SeriesNotification;
  noSeriesLabel?: string;
};

type NavigationHandlers = {
  goToMarketing: (bookGuid: string) => void;
  goToAudiobook: (bookGuid: string) => void;
  goToSeriesManage: (seriesName: string | null | undefined, seriesId?: string | null) => boolean;
};

export const createBookNavigationHandlers = ({
  navigate,
  notifyStandaloneSeries,
  noSeriesLabel = NO_SERIES_LABEL,
}: NavigationHandlerDeps): NavigationHandlers => {
  const goToMarketing = (bookGuid: string) => {
    if (!bookGuid) return;
    navigate(`/book-detail/${encodeURIComponent(bookGuid)}?tab=marketing`);
  };

  const goToAudiobook = (bookGuid: string) => {
    if (!bookGuid) return;
    navigate(`/audio/${bookGuid}`);
  };

  const goToSeriesManage = (seriesName: string | null | undefined, seriesId?: string | null) => {
    if (!seriesName || seriesName === noSeriesLabel) {
      notifyStandaloneSeries('Standalone books do not have shared series settings.');
      return false;
    }
    const seriesIdQuery = seriesId ? `seriesId=${encodeURIComponent(seriesId)}&` : '';
    navigate(`/series/manage?${seriesIdQuery}seriesName=${encodeURIComponent(seriesName)}`);
    return true;
  };

  return { goToMarketing, goToAudiobook, goToSeriesManage };
};

type ReplaceFileValidationResult =
  | { allowed: true }
  | {
      allowed: false;
      message: string;
      toastLevel: 'error' | 'warning';
      toastDuration?: number;
      applyFieldError?: boolean;
    };

export const validateReplaceFileSelection = ({
  fileName,
  hasTarget,
}: {
  fileName: string;
  hasTarget: boolean;
}): ReplaceFileValidationResult => {
  const lowerName = (fileName || '').toLowerCase();
  const isSupported = lowerName.endsWith('.docx') || lowerName.endsWith('.epub');
  if (!isSupported) {
    return {
      allowed: false,
      message: 'Only .docx or .epub files are supported',
      toastLevel: 'error',
      toastDuration: 5,
      applyFieldError: true,
    };
  }
  if (!hasTarget) {
    return {
      allowed: false,
      message: 'Select a book before uploading a manuscript.',
      toastLevel: 'error',
    };
  }
  return { allowed: true };
};

export type ReplaceSubmitValidationResult =
  | { valid: true }
  | {
      valid: false;
      replaceError?: string;
      toast?: { level: 'error' | 'info' | 'warning'; message: string; duration?: number };
    };

export const validateReplaceSubmission = ({
  user,
  replaceTargetBook,
  replaceFile,
  isReplacePreviewing,
  replacePreviewToken,
  replacePreviewRows,
}: {
  user: User | null;
  replaceTargetBook: BookRecord | null;
  replaceFile: File | null;
  isReplacePreviewing: boolean;
  replacePreviewToken: string | null;
  replacePreviewRows: ReplacePreviewRow[];
}): ReplaceSubmitValidationResult => {
  if (!user) {
    return {
      valid: false,
      toast: { level: 'error', message: 'Please sign in before replacing the manuscript', duration: 5 },
    };
  }
  if (!replaceTargetBook) {
    return {
      valid: false,
      toast: { level: 'error', message: 'Select a book before replacing the manuscript', duration: 5 },
    };
  }
  if (!replaceFile) {
    return {
      valid: false,
      replaceError: 'Please select a manuscript file to upload',
    };
  }
  if (isReplacePreviewing) {
    return {
      valid: false,
      toast: { level: 'info', message: 'Please wait while we finish generating the preview.' },
    };
  }
  if (!replacePreviewToken) {
    const msg = 'Generate a preview before replacing the manuscript.';
    return {
      valid: false,
      replaceError: msg,
      toast: { level: 'warning', message: msg, duration: 5 },
    };
  }
  if (!replacePreviewRows.length) {
    const msg = 'Cannot replace the manuscript because no chapters were detected in the preview.';
    return {
      valid: false,
      replaceError: msg,
      toast: { level: 'error', message: msg, duration: 6 },
    };
  }
  return { valid: true };
};

interface ErrorResponse {
  message?: string;
  status?: string;
}

export interface BookItemWithMenuProps {
  book: BookRecord;
  onDelete: (event: React.MouseEvent<HTMLElement>, bookGuid: string) => void;
  onReplace: (bookGuid: string) => void;
  onTranslate: (bookGuid: string) => void;
  isRecalculating: boolean;
  onMarketing: (bookGuid: string) => void;
  onAudio: (bookGuid: string) => void;
  onRefreshProgress: (bookGuid: string) => void;
  allPossibleLanguages: string[];
  showTranslatedFlags: boolean;
  flagsSettingEnabled: boolean;
  isRefreshingProgress: boolean;
  manualCompletedLanguages: string[];
  onMarkLanguageComplete: (bookGuid: string, language: string) => void;
  onUnmarkLanguageComplete: (bookGuid: string, language: string) => void;
  onRequestDetails?: (bookGuid: string) => void;
}

function countryCodeToFlagEmoji(code: string): string {
  return code
    .toUpperCase()
    .split('')
    .map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65))
    .join('');
}

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

const getTranslationFlagDisplay = ({
  book,
  allPossibleLanguages,
  manualCompletedLanguages,
}: {
  book: BookRecord;
  allPossibleLanguages: string[];
  manualCompletedLanguages: string[];
}): TranslationFlagDisplay[] => {
  const manualSet = new Set(manualCompletedLanguages || []);
  const normalizedTranslationProgress = canonicalizeTranslationProgressMap(book.translation_progress);
  return allPossibleLanguages
    .filter(language => canonicalizeLanguageName(language) !== canonicalizeLanguageName(book.language))
    .filter(language => {
      if (language === 'Spanish') {
        const progressInfo = normalizedTranslationProgress?.[canonicalizeLanguageName(language)];
        const isManuallyComplete = manualSet.has(language);
        return isManuallyComplete || (progressInfo && progressInfo.status !== 'not_started');
      }
      return true;
    })
    .map(language => {
      const countryCode = getLanguageCountryCode(language) || '';
      const progressInfo = normalizedTranslationProgress?.[canonicalizeLanguageName(language)];
      const isManuallyComplete = manualSet.has(language);
      return {
        language,
        countryCode,
        status: (isManuallyComplete ? 'complete' : (progressInfo?.status || 'not_started')) as 'complete' | 'in_progress' | 'not_started',
        percentage: isManuallyComplete ? 100 : (progressInfo?.percentage || 0),
        translated_chapters: isManuallyComplete ? (progressInfo?.total_chapters || 0) : (progressInfo?.translated_chapters || 0),
        total_chapters: progressInfo?.total_chapters || 0,
        isAvailable: isManuallyComplete ? true : (progressInfo?.status === 'complete'),
        isInProgress: isManuallyComplete ? false : (progressInfo?.status === 'in_progress'),
        isManuallyComplete,
      };
    });
};

const hasTranslationsStarted = (book: BookRecord, manualCompletedLanguages: string[] = []) => {
  if (manualCompletedLanguages.length > 0) return true;
  const normalizedTranslationProgress = canonicalizeTranslationProgressMap(book.translation_progress);
  return Object.values(normalizedTranslationProgress || {}).some(entry => entry?.status && entry.status !== 'not_started');
};

const getTranslationSummary = ({
  isRefreshingProgress,
  showFlagsLoading,
  flagStatus,
  progressState,
  flagsDisplay,
}: {
  isRefreshingProgress: boolean;
  showFlagsLoading: boolean;
  flagStatus?: BookRecord['flagStatus'];
  progressState?: BookRecord['progress_state'];
  flagsDisplay: TranslationFlagDisplay[];
}) => {
  if (isRefreshingProgress) return 'Refreshing flags';
  if (showFlagsLoading) return 'Loading status';
  if (progressState === 'unavailable') return 'Status unavailable';
  if (flagStatus === 'error') return 'Flags unavailable';
  const complete = flagsDisplay.filter(flag => flag.status === 'complete').length;
  const inProgress = flagsDisplay.filter(flag => flag.status === 'in_progress').length;
  const parts: string[] = [];
  if (complete > 0) parts.push(`${complete} complete`);
  if (inProgress > 0) parts.push(`${inProgress} in progress`);
  return parts.length ? parts.join(' · ') : 'No translations yet';
};

type TranslationFlagsPopoverContentProps = {
  bookGuid: string;
  flagsDisplay: TranslationFlagDisplay[];
  normalizedTranslationProgress: ReturnType<typeof canonicalizeTranslationProgressMap>;
  onMarkLanguageComplete: (bookGuid: string, language: string) => void;
  onUnmarkLanguageComplete: (bookGuid: string, language: string) => void;
};

type TranslationFlagRailProps = TranslationFlagsPopoverContentProps & {
  variant?: 'card' | 'list' | 'popover';
  maxVisible?: number;
};

const TranslationFlagRail: React.FC<TranslationFlagRailProps> = ({
  bookGuid,
  flagsDisplay,
  normalizedTranslationProgress,
  onMarkLanguageComplete,
  onUnmarkLanguageComplete,
  variant = 'popover',
  maxVisible,
}) => {
  const displayFlags = useMemo(() => {
    if (variant !== 'list') return flagsDisplay;
    const statusPriority = { in_progress: 0, complete: 1, not_started: 2 };
    return flagsDisplay
      .map((flag, index) => ({ flag, index }))
      .sort((a, b) => statusPriority[a.flag.status] - statusPriority[b.flag.status] || a.index - b.index)
      .map(({ flag }) => flag);
  }, [flagsDisplay, variant]);
  const visibleFlags = typeof maxVisible === 'number' ? displayFlags.slice(0, maxVisible) : displayFlags;
  const hiddenCount = Math.max(0, displayFlags.length - visibleFlags.length);

  return (
    <FlagList
      className={`manage-books-flag-rail manage-books-flag-rail--${variant}`}
      data-testid={`tutorial:manage-books:translation-status:${bookGuid}`}
    >
      {visibleFlags.map(({ language, countryCode, status, percentage, isManuallyComplete }) => (
        <Tooltip
          key={language}
          destroyOnHidden
          mouseEnterDelay={0.2}
          title={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{language}</div>
              {status === 'complete' ? (
                <div className="manage-books-tooltip-status is-ready">Complete</div>
              ) : status === 'in_progress' ? (
                <>
                  <div>{percentage}% complete</div>
                  <div style={{ fontSize: '0.85em', opacity: 0.8 }}>
                    {normalizedTranslationProgress?.[canonicalizeLanguageName(language)]?.translated_chapters || 0} of {normalizedTranslationProgress?.[canonicalizeLanguageName(language)]?.total_chapters || 0} chapters
                  </div>
                </>
              ) : (
                <div style={{ opacity: 0.6 }}>Not started</div>
              )}
            </div>
          }
        >
          <Dropdown
            menu={{
              items: [
                (!isManuallyComplete && status !== 'complete') && { key: 'mark', label: 'Mark as complete', icon: <CheckCircleOutlined style={{ color: 'var(--text-success)' }} /> },
                (isManuallyComplete) && { key: 'unmark', label: 'Remove manual mark', icon: <CloseCircleOutlined style={{ color: 'var(--text-danger)' }} /> },
              ].filter(Boolean) as any,
              onClick: ({ key }) => {
                if (key === 'mark') { onMarkLanguageComplete(bookGuid, language); }
                else if (key === 'unmark') { onUnmarkLanguageComplete(bookGuid, language); }
              }
            }}
            trigger={["click"]}
            placement="bottomLeft"
          >
            <FlagWrapper>
              <FlagIcon
                data-country={countryCode}
                $status={status as 'complete' | 'in_progress' | 'not_started'}
                aria-label={`${language} translation flag: ${status.replace('_', ' ')}`}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.currentTarget.click();
                  }
                }}
              >
                {language === 'Hebrew' ? 'א' : null}
              </FlagIcon>
            </FlagWrapper>
          </Dropdown>
        </Tooltip>
      ))}
      {hiddenCount > 0 && (
        variant === 'list' || variant === 'card' ? (
          <Popover
            trigger={['hover', 'focus', 'click']}
            placement={variant === 'card' ? 'bottomLeft' : 'bottom'}
            overlayClassName="manage-books-flags-overlay"
            content={
              <TranslationFlagsPopoverContent
                bookGuid={bookGuid}
                flagsDisplay={flagsDisplay}
                normalizedTranslationProgress={normalizedTranslationProgress}
                onMarkLanguageComplete={onMarkLanguageComplete}
                onUnmarkLanguageComplete={onUnmarkLanguageComplete}
              />
            }
          >
            <button
              type="button"
              className="manage-books-flag-overflow"
              aria-label={`Show ${hiddenCount} more language statuses`}
              onClick={(event) => event.stopPropagation()}
            >
              +{hiddenCount}
            </button>
          </Popover>
        ) : (
          <span className="manage-books-flag-overflow" aria-label={`${hiddenCount} more language statuses`}>
            +{hiddenCount}
          </span>
        )
      )}
    </FlagList>
  );
};

const TranslationFlagsPopoverContent: React.FC<TranslationFlagsPopoverContentProps> = (props) => (
  <div className="manage-books-flags-popover">
    <TranslationFlagRail {...props} variant="popover" />
  </div>
);

type BookGridItemData = {
  books: BookRecord[];
  columnCount: number;
  renderBook: (book: BookRecord) => React.ReactNode;
  columnWidth: number;
  rowHeight: number;
  horizontalOffset: number;
};

interface VirtualBookGridProps {
  books: BookRecord[];
  renderBook: (book: BookRecord) => React.ReactNode;
  onVisibleRange?: (startIndex: number, endIndex: number) => void;
  maxInitialVisible?: number;
  fillViewport?: boolean;
  forceVirtualize?: boolean;
  centerItems?: boolean;
}

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const VirtualBookGrid: React.FC<VirtualBookGridProps> = ({
  books,
  renderBook,
  onVisibleRange,
  maxInitialVisible = 36,
  fillViewport = false,
  forceVirtualize = false,
  centerItems = false,
}) => {
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const rafIdRef = React.useRef<number | null>(null);

  useIsomorphicLayoutEffect(() => {
    const node = innerRef.current;
    if (!node) return;

    const updateSize = () => {
      const next = node.clientWidth;
      const nextHeight = node.clientHeight;
      setWidth(prev => (prev === next ? prev : next));
      setHeight(prev => (prev === nextHeight ? prev : nextHeight));
    };

    updateSize();

    const scheduleUpdate = () => {
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        if (rafIdRef.current !== null) {
          window.cancelAnimationFrame(rafIdRef.current);
        }
        rafIdRef.current = window.requestAnimationFrame(() => {
          rafIdRef.current = null;
          updateSize();
        });
      } else {
        updateSize();
      }
    };

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(entries => {
        if (!Array.isArray(entries) || entries.length === 0) {
          return;
        }
        scheduleUpdate();
      });
      observer.observe(node);
      return () => {
        if (rafIdRef.current !== null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        observer.disconnect();
      };
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => {
        if (rafIdRef.current !== null && typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        window.removeEventListener('resize', updateSize);
      };
    }

    return;
  }, []);

  const columnGap = CARD_GAP;
  const rowGap = CARD_GAP;
  const columnWidth = CARD_WIDTH + columnGap;
  const rowHeight = CARD_HEIGHT + rowGap;
  const columnCount = width ? Math.max(1, Math.floor((width + columnGap) / columnWidth)) : 1;
  const rowCount = Math.ceil(books.length / columnCount);
  const visualGridWidth = columnCount * CARD_WIDTH + Math.max(0, columnCount - 1) * columnGap;
  const horizontalOffset = centerItems ? Math.max(0, Math.floor((width - visualGridWidth) / 2)) : 0;

  const shouldVirtualize = (forceVirtualize || books.length >= VIRTUALIZATION_THRESHOLD) && width > 0;
  const effectiveHeight = Math.max(rowHeight, rowCount * rowHeight);
  const availableHeight = fillViewport && height > 0 ? height : MAX_VIRTUALIZED_HEIGHT;
  const gridHeight = shouldVirtualize
    ? Math.min(availableHeight, MAX_VIRTUALIZED_HEIGHT, effectiveHeight)
    : effectiveHeight;

  const handleItemsRendered = useCallback(
    ({
      visibleRowStartIndex,
      visibleRowStopIndex,
      visibleColumnStartIndex,
      visibleColumnStopIndex,
    }: {
      visibleRowStartIndex: number;
      visibleRowStopIndex: number;
      visibleColumnStartIndex: number;
      visibleColumnStopIndex: number;
    }) => {
      if (!onVisibleRange || books.length === 0) return;
      const startIndex = visibleRowStartIndex * columnCount + visibleColumnStartIndex;
      const endIndex = visibleRowStopIndex * columnCount + visibleColumnStopIndex;
      onVisibleRange(startIndex, Math.min(endIndex, books.length - 1));
    },
    [onVisibleRange, books.length, columnCount]
  );

  useEffect(() => {
    if (!onVisibleRange || shouldVirtualize) return;
    if (books.length === 0) return;
    const endIndex = Math.min(books.length - 1, Math.max(0, maxInitialVisible - 1));
    onVisibleRange(0, endIndex);
  }, [books, onVisibleRange, shouldVirtualize, maxInitialVisible]);

  const itemData = useMemo<BookGridItemData>(
    () => ({
      books,
      columnCount,
      renderBook,
      columnWidth,
      rowHeight,
      horizontalOffset,
    }),
    [books, columnCount, renderBook, columnWidth, rowHeight, horizontalOffset]
  );

  const cellRenderer = useCallback(
    ({ columnIndex, rowIndex, style, data }: GridChildComponentProps<BookGridItemData>) => {
      const { books: dataset, columnCount: cols, renderBook: render, columnWidth: colWidth, rowHeight: rowH, horizontalOffset: offset } = data;
      const itemIndex = rowIndex * cols + columnIndex;
      if (itemIndex >= dataset.length) return null;
      const book = dataset[itemIndex];
      const left = typeof style.left === 'number' ? style.left + offset : style.left;
      return (
        <div
          style={{
            ...style,
            left,
            width: colWidth,
            height: rowH,
            paddingRight: columnGap,
            paddingBottom: rowGap,
          }}
        >
          <div style={{ width: CARD_WIDTH }}>
            {render(book)}
          </div>
        </div>
      );
    },
    [columnGap, rowGap]
  );

  return (
    <BookGridWrapper className="manage-books-book-grid-wrapper">
      <div
        ref={innerRef}
        className={`manage-books-book-grid-measure${fillViewport ? ' manage-books-book-grid-measure--fill' : ''}${centerItems ? ' manage-books-book-grid-measure--center' : ''}`}
      >
        {shouldVirtualize && rowCount * rowHeight > gridHeight ? (
          <VirtualGrid
            columnCount={columnCount}
            columnWidth={columnWidth}
            height={gridHeight}
            rowCount={rowCount}
            rowHeight={rowHeight}
            width={width}
            itemData={itemData}
            onItemsRendered={handleItemsRendered}
          >
            {cellRenderer}
          </VirtualGrid>
        ) : (
          <BookGrid className="manage-books-book-grid">
            {books.map(book => {
              const rendered = renderBook(book);
              if (React.isValidElement(rendered)) {
                return React.cloneElement(rendered, { key: book.bookguid });
              }
              return (
                <React.Fragment key={book.bookguid}>
                  {rendered}
                </React.Fragment>
              );
            })}
          </BookGrid>
        )}
      </div>
    </BookGridWrapper>
  );
};

type ProcessedBook = {
  book: BookRecord;
  titleLower: string;
  authorLower: string;
  seriesLower: string;
  searchValue: string;
};

type ManageBooksViewMode = 'covers' | 'list';
type ManageBooksSortMode = 'author' | 'title' | 'series';
type ManageBooksGroupByMode = 'none' | 'author' | 'series' | 'author_series';
type ManageBooksFlatGroupByMode = Extract<ManageBooksGroupByMode, 'author' | 'series'>;

type LibraryBookGroup = {
  key: string;
  label: string;
  books: BookRecord[];
};

const UNKNOWN_AUTHOR_LABEL = 'Unknown author';
const STANDALONE_GROUP_LABEL = 'Standalone';

const getBookCountLabel = (count: number) => `${count} ${count === 1 ? 'book' : 'books'}`;

const normalizeLibraryGroupAuthor = (value: string | null | undefined): string => {
  const trimmed = (value || '').replace(/\s+/g, ' ').trim();
  return trimmed.length > 0 ? trimmed : UNKNOWN_AUTHOR_LABEL;
};

const normalizeLibraryGroupSeries = (value: string | null | undefined): string => {
  const normalized = normalizeLibrarySeriesName(value, '');
  return normalized.length > 0 ? normalized : STANDALONE_GROUP_LABEL;
};

const buildLibraryBookGroups = (
  books: BookRecord[],
  groupBy: ManageBooksFlatGroupByMode
): LibraryBookGroup[] => {
  const groups = new Map<string, LibraryBookGroup>();

  books.forEach(book => {
    const label = groupBy === 'author'
      ? normalizeLibraryGroupAuthor(book.author)
      : normalizeLibraryGroupSeries(book.series);
    const key = `${groupBy}:${label.toLocaleLowerCase()}`;
    const existing = groups.get(key);
    if (existing) {
      existing.books.push(book);
      return;
    }
    groups.set(key, { key, label, books: [book] });
  });

  return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const isManageBooksViewMode = (value: unknown): value is ManageBooksViewMode =>
  value === 'covers' || value === 'list';

const isManageBooksSortMode = (value: unknown): value is ManageBooksSortMode =>
  value === 'author' || value === 'title' || value === 'series';

const isManageBooksGroupByMode = (value: unknown): value is ManageBooksGroupByMode =>
  value === 'none' || value === 'author' || value === 'series' || value === 'author_series';

const getManageBooksViewModeStorageKey = (userId: string | null | undefined) =>
  userId ? `manageBooksViewMode:${userId}` : null;

const getManageBooksSortModeStorageKey = (userId: string | null | undefined) =>
  userId ? `manageBooksSortMode:${userId}` : null;

const getManageBooksGroupByModeStorageKey = (userId: string | null | undefined) =>
  userId ? `manageBooksGroupByMode:${userId}` : null;

const readStoredManageBooksOption = <T extends string>(
  storageKey: string | null,
  validator: (value: unknown) => value is T
): T | null => {
  if (!storageKey || typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(storageKey);
    return validator(stored) ? stored : null;
  } catch {
    return null;
  }
};

const readStoredManageBooksViewMode = (userId: string | null | undefined): ManageBooksViewMode | null =>
  readStoredManageBooksOption(getManageBooksViewModeStorageKey(userId), isManageBooksViewMode);

const readStoredManageBooksSortMode = (userId: string | null | undefined): ManageBooksSortMode | null =>
  readStoredManageBooksOption(getManageBooksSortModeStorageKey(userId), isManageBooksSortMode);

const readStoredManageBooksGroupByMode = (userId: string | null | undefined): ManageBooksGroupByMode | null =>
  readStoredManageBooksOption(getManageBooksGroupByModeStorageKey(userId), isManageBooksGroupByMode);

const persistManageBooksViewMode = (
  userId: string | null | undefined,
  viewMode: ManageBooksViewMode
) => {
  persistManageBooksOption(getManageBooksViewModeStorageKey(userId), viewMode);
};

const persistManageBooksOption = (storageKey: string | null, value: string) => {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(storageKey, value);
  } catch {
    // Local preference caching is best-effort; the backend remains canonical.
  }
};

const persistManageBooksSortMode = (
  userId: string | null | undefined,
  sortMode: ManageBooksSortMode
) => {
  persistManageBooksOption(getManageBooksSortModeStorageKey(userId), sortMode);
};

const persistManageBooksGroupByMode = (
  userId: string | null | undefined,
  groupByMode: ManageBooksGroupByMode
) => {
  persistManageBooksOption(getManageBooksGroupByModeStorageKey(userId), groupByMode);
};

type TranslationFlagDisplay = {
  language: string;
  countryCode: string;
  status: 'complete' | 'in_progress' | 'not_started';
  percentage: number;
  translated_chapters: number;
  total_chapters: number;
  isAvailable: boolean;
  isInProgress: boolean;
  isManuallyComplete: boolean;
};

export type FetchBooksOptions = {
  rethrow?: boolean;
  suppressErrorToast?: boolean;
  skipLoadingIndicator?: boolean;
};

type FetchBooksDeps = {
  user: User | null;
  startTransition: (cb: () => void) => void;
  setBookDetails: React.Dispatch<React.SetStateAction<BookRecord[]>>;
  setManualCompleteMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  setLoadingTip: (value: string) => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  messageError: (text: string) => void;
  makeRequest: () => Promise<{ data: IBook[] }>;
  onBooksLoaded?: (books: IBook[]) => void;
};

export const createFetchBooks =
  ({
    user,
    startTransition,
    setBookDetails,
    setManualCompleteMap,
    setLoadingTip,
    setIsLoading,
    messageError,
    makeRequest,
    onBooksLoaded,
  }: FetchBooksDeps) =>
  async (options?: FetchBooksOptions) => {
    const skipLoader = !!options?.skipLoadingIndicator;
    if (!skipLoader) {
      setLoadingTip('Loading your library...');
      setIsLoading(true);
    }
    if (!user) {
      console.error("User is not authenticated");
      if (!skipLoader) {
        setIsLoading(false);
      }
      return;
    }
    try {
      const response = await makeRequest();
      startTransition(() => {
        setBookDetails(response.data.map(book => enhanceBook({ ...book })));
        const map: Record<string, string[]> = {};
        for (const b of response.data) {
          if (b.manual_completed_languages && b.manual_completed_languages.length) {
            map[b.bookguid] = [...b.manual_completed_languages];
          }
        }
        setManualCompleteMap(map);
      });
      if (onBooksLoaded) {
        onBooksLoaded(response.data);
      }
    } catch (error) {
      console.error("Error fetching books: ", (error as AxiosError).response?.data || (error as AxiosError).message);
      if (!options?.suppressErrorToast) {
        messageError('Failed to load your books. Please try again.');
      }
      if (options?.rethrow) {
        throw error;
      }
    } finally {
      if (!skipLoader) {
        setIsLoading(false);
      }
    }
  };


export type UploadTourAutoOpenState = {
  uploadTourEnabled: boolean;
  uploadTourFlagsChecked: boolean;
  uploadTourOpen: boolean;
  hasUser: boolean;
  isLoading: boolean;
  pageDisabled: boolean;
  hasBooks: boolean;
};

export const shouldAutoOpenUploadTour = ({
  uploadTourEnabled,
  uploadTourFlagsChecked,
  uploadTourOpen,
  hasUser,
  isLoading,
  pageDisabled,
  hasBooks,
}: UploadTourAutoOpenState) =>
  uploadTourEnabled &&
  uploadTourFlagsChecked &&
  !uploadTourOpen &&
  hasUser &&
  !isLoading &&
  !pageDisabled &&
  !hasBooks;

export type UploadTourAutoCloseState = {
  uploadTourOpen: boolean;
  hasBooks: boolean;
  uploadTourManual: boolean;
};

export const shouldAutoCloseUploadTour = ({
  uploadTourOpen,
  hasBooks,
  uploadTourManual,
}: UploadTourAutoCloseState) => uploadTourOpen && hasBooks && !uploadTourManual;

export type SeriesBootstrapAutoRunState = {
  hasUser: boolean;
  seriesBootstrapStatus: 'unchecked' | 'pending' | 'done';
  seriesBootstrapInFlight: boolean;
  hasBooks: boolean;
};

export const shouldRunSeriesBootstrap = ({
  hasUser,
  seriesBootstrapStatus,
  seriesBootstrapInFlight,
  hasBooks,
}: SeriesBootstrapAutoRunState) =>
  hasUser &&
  seriesBootstrapStatus === 'pending' &&
  !seriesBootstrapInFlight &&
  hasBooks;

export type SeriesBootstrapDeps = {
  apiBase: string;
  user: User | null;
  books: IBook[];
  axiosInstance: typeof axios;
  getAuthHeadersFn: typeof getAuthHeaders;
  noSeriesLabel: string;
  seriesBootstrapInFlight: React.MutableRefObject<boolean>;
  setLoadingTip: (value: string) => void;
  setIsBootstrapLoading: (value: boolean) => void;
  setSeriesBootstrapStatus: (status: 'unchecked' | 'pending' | 'done') => void;
  fetchBooks: (options?: FetchBooksOptions) => Promise<void>;
};

export const runSeriesBootstrap = async ({
  apiBase,
  user,
  books,
  axiosInstance,
  getAuthHeadersFn,
  noSeriesLabel,
  seriesBootstrapInFlight,
  setLoadingTip,
  setIsBootstrapLoading,
  setSeriesBootstrapStatus,
  fetchBooks,
}: SeriesBootstrapDeps) => {
  if (!user) return;
  if (seriesBootstrapInFlight.current) return;
  seriesBootstrapInFlight.current = true;
  setLoadingTip('Linking books to series (one-time setup)...');
  setIsBootstrapLoading(true);

  try {
    const headers = await Promise.resolve(getAuthHeadersFn(user));

    let seriesList: SeriesSummary[] = [];
    try {
      const res = await axiosInstance.get<SeriesSummary[]>(`${apiBase}/series`, {
        params: { user_id: user.uid },
        headers,
      });
      seriesList = res.data || [];
    } catch (error) {
      console.warn('Could not load existing series list for bootstrap', error);
      throw error;
    }

    const seriesByName = new Map<string, SeriesSummary>();
    const ambiguousNames = new Set<string>();
    seriesList.forEach(item => {
      if (item.name) {
        const key = normalizeLibrarySeriesName(item.name).toLowerCase();
        if (seriesByName.has(key)) ambiguousNames.add(key);
        seriesByName.set(key, item);
      }
    });

    const booksWithSeries = books.filter(
      b => !b.seriesId && normalizeLibrarySeriesName(b.series, '') && normalizeLibrarySeriesName(b.series) !== noSeriesLabel
    );
    if (booksWithSeries.length === 0) {
      setSeriesBootstrapStatus('done');
      try {
        await axiosInstance.post(`${apiBase}/user/ui-flags/set`, {
          userId: user.uid,
          flags: { seriesBootstrapDone: true },
        });
      } catch (_e) {
        // best effort
      }
      return;
    }

    const normalizeAuthor = (value: string | undefined | null) =>
      (value || '').trim().toLowerCase();

    const groups = new Map<string, IBook[]>();
    booksWithSeries.forEach(book => {
      const name = normalizeLibrarySeriesName(book.series);
      const key = name.toLowerCase();
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(book);
    });

    for (const [key, groupedBooks] of groups.entries()) {
      if (ambiguousNames.has(key)) {
        throw new Error('Multiple series have this name. Select the intended series from the book.');
      }
      const name = normalizeLibrarySeriesName(groupedBooks[0].series);
      const firstAuthor = normalizeAuthor(groupedBooks[0].author);
      let seriesId = seriesByName.get(key)?.seriesId || null;
      if (!seriesId) {
        try {
          const authorName = groupedBooks[0].author || '';
          const createRes = await axiosInstance.post(
            `${apiBase}/series`,
            {
              userId: user.uid,
              name,
              creationKey: `implicit-name-v1:${name.toLowerCase()}`,
              description: '',
              author: authorName,
              defaults: { author: authorName },
              defaultsPopulated: false,
            },
            { headers }
          );
          seriesId = createRes.data?.seriesId;
          if (seriesId) {
            seriesByName.set(key, { seriesId, name });
          }
        } catch (error) {
          console.warn('Failed to create series during bootstrap', name, error);
          continue;
        }
      }

      if (!seriesId) continue;

      for (const book of groupedBooks) {
        const bookAuthor = normalizeAuthor(book.author);
        if (firstAuthor && bookAuthor && bookAuthor !== firstAuthor) {
          continue;
        }
        try {
          await axiosInstance.post(
            `${apiBase}/series/assign`,
            {
              userId: user.uid,
              seriesId,
              bookId: book.bookguid,
              inheritance: {},
              onlyIfUnassigned: true,
            },
            { headers }
          );
        } catch (error) {
          console.warn('Failed to attach book during bootstrap', book.bookguid, error);
        }
      }
    }

    try {
      await axiosInstance.post(`${apiBase}/user/ui-flags/set`, {
        userId: user.uid,
        flags: { seriesBootstrapDone: true },
      });
    } catch (error) {
      console.warn('Failed to persist series bootstrap flag', error);
    }

    setSeriesBootstrapStatus('done');
    fetchBooks({ suppressErrorToast: true, skipLoadingIndicator: true });
  } finally {
    seriesBootstrapInFlight.current = false;
    setIsBootstrapLoading(false);
  }
};

const BookItemWithMenuComponent: React.FC<BookItemWithMenuProps> = ({
  book,
  onDelete,
  onReplace,
  onTranslate,
  isRecalculating,
  onMarketing,
  onAudio,
  onRefreshProgress,
  allPossibleLanguages,
  showTranslatedFlags,
  flagsSettingEnabled,
  isRefreshingProgress,
  manualCompletedLanguages,
  onMarkLanguageComplete,
  onUnmarkLanguageComplete,
  onRequestDetails,
}) => {
  const [failedCoverUrl, setFailedCoverUrl] = useState<string | null>(null);
  const hasDisplayableCover = !!book.coverUrl && failedCoverUrl !== book.coverUrl;
  const cardRef = useRef<HTMLDivElement | null>(null);
  const requestedRef = useRef(false);
  const bookTitle = book.title.length > 20 ? `${book.title.substring(0, 20)}...` : book.title;

  const normalizedTranslationProgress = useMemo(
    () => canonicalizeTranslationProgressMap(book.translation_progress),
    [book.translation_progress],
  );
  const showFlagsLoading =
    book.needs_translation_refresh ||
      book.progress_state === 'refreshing' ||
      (!book.progress_state && (
        !book.detailsLoaded ||
        book.flagStatus === 'loading' ||
        book.flagStatus == null
      ));
  const statusUnavailable = book.progress_state === 'unavailable' || book.flagStatus === 'error';
  const flagsDisplay = useMemo(
    () => getTranslationFlagDisplay({
      book,
      allPossibleLanguages,
      manualCompletedLanguages,
    }),
    [book, allPossibleLanguages, manualCompletedLanguages]
  );
  const translationSummary = useMemo(
    () => getTranslationSummary({
      isRefreshingProgress,
      showFlagsLoading,
      flagStatus: book.flagStatus,
      progressState: book.progress_state,
      flagsDisplay,
    }),
    [book.flagStatus, book.progress_state, flagsDisplay, isRefreshingProgress, showFlagsLoading]
  );
  const showFlagsPopover =
    !isRefreshingProgress &&
    !showFlagsLoading &&
    book.flagStatus !== 'error' &&
    flagsDisplay.length > 0;


  const getFileTypeIcon = useCallback(() => {
    if (!book.uploadType) return null;
    switch (book.uploadType.toLowerCase()) {
      case 'word': return <FileWordOutlined style={{ fontSize: 16, color: 'var(--text-info)' }} />;
      case 'epub': return <ReadOutlined style={{ fontSize: 16, color: 'var(--text-success)' }} />;
      case 'pdf':  return <FilePdfOutlined style={{ fontSize: 16, color: 'var(--text-danger)' }} />;
      default:     return <BookOutlined style={{ fontSize: 16, color: 'var(--text-secondary)' }} />;
    }
  }, [book.uploadType]);

  const handleTranslateClick = useCallback(() => {
    if (!isRecalculating) {
      onTranslate(book.bookguid);
    }
  }, [isRecalculating, onTranslate, book.bookguid]);

  const handleCoverKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    handleTranslateClick();
  }, [handleTranslateClick]);

  useEffect(() => {
    requestedRef.current = false;
  }, [book.bookguid]);

  useEffect(() => {
    if (!onRequestDetails) return;
    if (book.detailsLoaded) return;
    if (requestedRef.current) return;
    const node = cardRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      requestedRef.current = true;
      onRequestDetails(book.bookguid);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          requestedRef.current = true;
          onRequestDetails(book.bookguid);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [book.bookguid, book.detailsLoaded, onRequestDetails]);

  const moreActionItems = useMemo(() => [
    flagsSettingEnabled && {
      key: 'refresh',
      label: isRefreshingProgress ? 'Refreshing flags' : 'Refresh flags',
      icon: <ReloadOutlined spin={isRefreshingProgress} />,
      disabled: isRefreshingProgress,
    },
    {
      key: 'replace',
      label: book.has_blocking_lock ? 'Replace unavailable' : 'Replace manuscript',
      icon: <UploadOutlined />,
      disabled: !!book.has_blocking_lock,
    },
    { key: 'delete', label: 'Delete book', icon: <DeleteOutlined />, danger: true },
  ].filter(Boolean) as any[], [book.has_blocking_lock, flagsSettingEnabled, isRefreshingProgress]);

  return (
    <BookItem
      className="book-item manage-books-book-card"
      ref={cardRef}
      data-testid={`tutorial:manage-books:book-card:${book.bookguid}`}
    >
      <BookCover
        data-testid={`tutorial:manage-books:book-cover:${book.bookguid}`}
        onClick={handleTranslateClick}
        onKeyDown={handleCoverKeyDown}
        aria-busy={isRecalculating}
        aria-label={`Open ${book.title}`}
        role="button"
        tabIndex={0}
      >
        {isRecalculating && (
          <CoverSpinner>
            <Spin size="large" />
          </CoverSpinner>
        )}
        {hasDisplayableCover ? (
          <BookCoverImg
            loading="lazy"
            decoding="async"
            key={book.coverUrl}
            src={book.coverUrl}
            onError={() => setFailedCoverUrl(book.coverUrl || null)}
            alt={`${book.title} cover`}
            width={140}
            height={220}
          />
        ) : (
          <BookTitle>
            <span className="manage-books-placeholder-title">{book.title}</span>
          </BookTitle>
        )}

        {book.uploadType && (
          <Tooltip title={book.uploadType === 'Word' ? 'Word' : book.uploadType} destroyOnHidden mouseEnterDelay={0.2}>
            <FileTypeBadge data-testid={`tutorial:manage-books:file-type:${book.bookguid}`}>{getFileTypeIcon()}</FileTypeBadge>
          </Tooltip>
        )}

      </BookCover>

      <BookCardMenu onClick={(event) => event.stopPropagation()}>
        <Dropdown
          trigger={['click']}
          menu={{
            items: moreActionItems,
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === 'refresh') onRefreshProgress(book.bookguid);
              if (key === 'replace') onReplace(book.bookguid);
              if (key === 'delete') onDelete(domEvent as React.MouseEvent<HTMLElement>, book.bookguid);
            },
          }}
        >
          <Button
            size="small"
            icon={<MoreOutlined />}
            aria-label={`More actions for ${book.title}`}
            data-testid={`tutorial:manage-books:book-card-more:${book.bookguid}`}
          />
        </Dropdown>
      </BookCardMenu>

      <div
        className="manage-books-card-footer"
        role="group"
        aria-label={`Book card summary for ${book.title}`}
        data-testid={`tutorial:manage-books:book-card-summary:${book.bookguid}`}
      >
        <BookText
          as="button"
          type="button"
          className="manage-books-card-title manage-books-title-action"
          onClick={handleTranslateClick}
          aria-label={`Open ${book.title}`}
          data-testid={`tutorial:manage-books:book-card-title:${book.bookguid}`}
        >
          {bookTitle}
        </BookText>

        <div
          className="manage-books-translation-compact"
          data-testid={`tutorial:manage-books:book-card-status:${book.bookguid}`}
        >
          {showTranslatedFlags && showFlagsPopover ? (
            <TranslationFlagRail
              bookGuid={book.bookguid}
              flagsDisplay={flagsDisplay}
              normalizedTranslationProgress={normalizedTranslationProgress}
              onMarkLanguageComplete={onMarkLanguageComplete}
              onUnmarkLanguageComplete={onUnmarkLanguageComplete}
              variant="card"
            />
          ) : showFlagsPopover ? (
              <Popover
                trigger={['hover', 'focus', 'click']}
                placement="bottomLeft"
                overlayClassName="manage-books-flags-overlay"
                content={
                  <TranslationFlagsPopoverContent
                    bookGuid={book.bookguid}
                    flagsDisplay={flagsDisplay}
                    normalizedTranslationProgress={normalizedTranslationProgress}
                    onMarkLanguageComplete={onMarkLanguageComplete}
                    onUnmarkLanguageComplete={onUnmarkLanguageComplete}
                  />
                }
              >
                <button
                  type="button"
                  className={`manage-books-translation-summary is-interactive ${
                    flagsDisplay.some(flag => flag.status === 'complete' || flag.status === 'in_progress')
                    ? 'is-active'
                    : ''
                  }`}
                  aria-haspopup="dialog"
                  aria-label={`Show translation flags for ${book.title}: ${translationSummary}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  {translationSummary}
                </button>
              </Popover>
          ) : statusUnavailable ? (
            <button
              type="button"
              className="manage-books-translation-summary is-error is-interactive"
              onClick={(event) => {
                event.stopPropagation();
                onRefreshProgress(book.bookguid);
              }}
              aria-label={`Retry translation status for ${book.title}`}
            >
              <ReloadOutlined />
              Status unavailable · Retry
            </button>
          ) : (
            <span
              className={`manage-books-translation-summary ${
                isRefreshingProgress || showFlagsLoading
                  ? 'is-loading'
                  : book.flagStatus === 'error'
                  ? 'is-error'
                  : flagsDisplay.some(flag => flag.status === 'complete' || flag.status === 'in_progress')
                  ? 'is-active'
                  : ''
              }`}
            >
              {(isRefreshingProgress || showFlagsLoading) && (
                <span className="manage-books-flag-spinner" aria-hidden="true" />
              )}
              {book.flagStatus === 'error' && <ReloadOutlined />}
              {translationSummary}
            </span>
          )}
        </div>
      </div>

    </BookItem>
  );
};

type LangProgress = NonNullable<IBook['translation_progress']>;

function progressShallowEqual(a?: LangProgress, b?: LangProgress): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    const av = a[k];
    const bv = b[k];
    if (!av || !bv) return false;
    if (av.status !== bv.status) return false;
    if (av.percentage !== bv.percentage) return false;
    if (av.translated_chapters !== bv.translated_chapters) return false;
    if (av.total_chapters !== bv.total_chapters) return false;
  }
  return true;
}

// Strong comparator to avoid re-renders when unrelated state updates
const areEqualBookItem = (prev: BookItemWithMenuProps, next: BookItemWithMenuProps): boolean => {
  if (prev.isRefreshingProgress !== next.isRefreshingProgress) return false;
  if (prev.showTranslatedFlags !== next.showTranslatedFlags) return false;
  if (prev.flagsSettingEnabled !== next.flagsSettingEnabled) return false;
  if (prev.isRecalculating !== next.isRecalculating) return false;
  if (prev.allPossibleLanguages !== next.allPossibleLanguages) return false;
  {
    const prevManual = (prev.manualCompletedLanguages || []).join('|');
    const nextManual = (next.manualCompletedLanguages || []).join('|');
    if (prevManual !== nextManual) return false;
  }

  const pb = prev.book;
  const nb = next.book;

  if (pb.bookguid !== nb.bookguid) return false;
  if (pb.title !== nb.title) return false;
  if (pb.coverUrl !== nb.coverUrl) return false;
  if (pb.uploadType !== nb.uploadType) return false;
  if (pb.language !== nb.language) return false;
  if ((pb.word_count_version ?? 0) !== (nb.word_count_version ?? 0)) return false;
  if (pb.flagStatus !== nb.flagStatus) return false;
  if (pb.detailsLoaded !== nb.detailsLoaded) return false;
  if (pb.has_blocking_lock !== nb.has_blocking_lock) return false;
  {
    const prevLocks = pb.active_blocking_locks || [];
    const nextLocks = nb.active_blocking_locks || [];
    if (prevLocks.length !== nextLocks.length) return false;
    for (let i = 0; i < prevLocks.length; i += 1) {
      if (prevLocks[i]?.id !== nextLocks[i]?.id) return false;
      if (prevLocks[i]?.status !== nextLocks[i]?.status) return false;
    }
  }

  if (!progressShallowEqual(pb.translation_progress, nb.translation_progress)) return false;

  if (prev.onDelete !== next.onDelete) return false;
  if (prev.onReplace !== next.onReplace) return false;
  if (prev.onTranslate !== next.onTranslate) return false;
  if (prev.onMarketing !== next.onMarketing) return false;
  if (prev.onAudio !== next.onAudio) return false;
  if (prev.onRefreshProgress !== next.onRefreshProgress) return false;
  if (prev.onMarkLanguageComplete !== next.onMarkLanguageComplete) return false;
  if (prev.onUnmarkLanguageComplete !== next.onUnmarkLanguageComplete) return false;
  if (prev.onRequestDetails !== next.onRequestDetails) return false;

  return true;
};

export const BookItemWithMenu = React.memo(BookItemWithMenuComponent, areEqualBookItem);

type BookListViewProps = {
  books: BookRecord[];
  ariaLabel?: string;
  onDelete: (event: React.MouseEvent<HTMLElement>, bookGuid: string) => void;
  onReplace: (bookGuid: string) => void;
  onTranslate: (bookGuid: string) => void;
  onSeriesManage: (seriesName: string | null | undefined, seriesId?: string | null) => boolean;
  onRefreshProgress: (bookGuid: string) => void;
  allPossibleLanguages: string[];
  showTranslatedFlags: boolean;
  flagsSettingEnabled: boolean;
  refreshingProgressIds: string[];
  manualCompleteMap: Record<string, string[]>;
  onMarkLanguageComplete?: (bookGuid: string, language: string) => void;
  onUnmarkLanguageComplete?: (bookGuid: string, language: string) => void;
  onRequestDetails?: (bookGuid: string) => void;
};

type BookListRowProps = Omit<
  BookListViewProps,
  'books' | 'ariaLabel' | 'refreshingProgressIds' | 'manualCompleteMap' | 'onMarkLanguageComplete' | 'onUnmarkLanguageComplete'
> & {
  book: BookRecord;
  isRefreshingProgress: boolean;
  manualCompletedLanguages: string[];
  onMarkLanguageComplete: (bookGuid: string, language: string) => void;
  onUnmarkLanguageComplete: (bookGuid: string, language: string) => void;
};

const BookListRow: React.FC<BookListRowProps> = ({
  book,
  onDelete,
  onReplace,
  onTranslate,
  onSeriesManage,
  onRefreshProgress,
  allPossibleLanguages,
  showTranslatedFlags,
  flagsSettingEnabled,
  isRefreshingProgress,
  manualCompletedLanguages,
  onMarkLanguageComplete,
  onUnmarkLanguageComplete,
  onRequestDetails,
}) => {
  const [failedCoverUrl, setFailedCoverUrl] = useState<string | null>(null);
  const hasDisplayableCover = !!book.coverUrl && failedCoverUrl !== book.coverUrl;
  const rowRef = useRef<HTMLDivElement | null>(null);
  const requestedRef = useRef(false);

  useEffect(() => {
    requestedRef.current = false;
  }, [book.bookguid]);

  useEffect(() => {
    if (!onRequestDetails) return;
    if (book.detailsLoaded) return;
    if (requestedRef.current) return;
    const node = rowRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      requestedRef.current = true;
      onRequestDetails(book.bookguid);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          requestedRef.current = true;
          onRequestDetails(book.bookguid);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: '400px 0px', threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [book.bookguid, book.detailsLoaded, onRequestDetails]);

  const showFlagsLoading =
    book.needs_translation_refresh ||
      book.progress_state === 'refreshing' ||
      (!book.progress_state && (
        !book.detailsLoaded ||
        book.flagStatus === 'loading' ||
        book.flagStatus == null
      ));
  const statusUnavailable = book.progress_state === 'unavailable' || book.flagStatus === 'error';
  const flagsDisplay = getTranslationFlagDisplay({
    book,
    allPossibleLanguages,
    manualCompletedLanguages,
  });
  const translationSummary = getTranslationSummary({
    isRefreshingProgress,
    showFlagsLoading,
    flagStatus: book.flagStatus,
    progressState: book.progress_state,
    flagsDisplay,
  });
  const normalizedTranslationProgress = canonicalizeTranslationProgressMap(book.translation_progress);
  const hasActiveTranslations = flagsDisplay.some(flag => flag.status === 'complete' || flag.status === 'in_progress');
  const showFlagsPopover =
    !isRefreshingProgress &&
    !showFlagsLoading &&
    book.flagStatus !== 'error' &&
    flagsDisplay.length > 0;
  const seriesName = book.series && book.series.trim() ? book.series : 'Standalone';
  const moreActionItems = [
    flagsSettingEnabled && {
      key: 'refresh',
      label: isRefreshingProgress ? 'Refreshing flags' : 'Refresh flags',
      icon: <ReloadOutlined spin={isRefreshingProgress} />,
      disabled: isRefreshingProgress,
    },
    {
      key: 'replace',
      label: 'Replace manuscript',
      icon: <UploadOutlined />,
      disabled: !!book.has_blocking_lock,
    },
    { key: 'delete', label: 'Delete book', icon: <DeleteOutlined />, danger: true },
  ].filter(Boolean) as any[];

  return (
    <div
      className="manage-books-list-row"
      role="row"
      key={book.bookguid}
      ref={rowRef}
      data-testid={`tutorial:manage-books:list-row:${book.bookguid}`}
    >
      <div className="manage-books-list-book-cell">
        <button
          type="button"
          className="manage-books-list-cover"
          onClick={() => onTranslate(book.bookguid)}
          aria-label={`Open ${book.title}`}
        >
          {hasDisplayableCover ? (
            <img key={book.coverUrl} src={book.coverUrl} alt={`${book.title} cover`} loading="lazy" onError={() => setFailedCoverUrl(book.coverUrl || null)} />
          ) : (
            <span>{(book.title || '?').slice(0, 1).toUpperCase()}</span>
          )}
        </button>
        <div className="manage-books-list-book-copy">
          <button
            type="button"
            className="manage-books-list-title manage-books-title-action"
            onClick={() => onTranslate(book.bookguid)}
            aria-label={`Open ${book.title || 'Untitled book'}`}
          >
            {book.title || 'Untitled book'}
          </button>
          <div className="manage-books-list-subtitle">{book.author || 'Unknown author'}</div>
        </div>
      </div>
      <div className="manage-books-list-meta">
        {book.series && book.series.trim() ? (
          <button
            type="button"
            className="manage-books-list-series-link"
            onClick={() => onSeriesManage(book.series, book.seriesId)}
          >
            {seriesName}
          </button>
        ) : (
          seriesName
        )}
      </div>
      <div className="manage-books-list-meta">
        {book.language || 'Unknown'}
        {book.uploadType && (
          <span
            className="manage-books-list-file-type"
            data-testid={`tutorial:manage-books:list-file-type:${book.bookguid}`}
          >
            {book.uploadType}
          </span>
        )}
      </div>
      <div className="manage-books-list-status">
        {showTranslatedFlags && showFlagsPopover ? (
          <TranslationFlagRail
            bookGuid={book.bookguid}
            flagsDisplay={flagsDisplay}
            normalizedTranslationProgress={normalizedTranslationProgress}
            onMarkLanguageComplete={onMarkLanguageComplete}
            onUnmarkLanguageComplete={onUnmarkLanguageComplete}
            variant="list"
            maxVisible={3}
          />
        ) : showFlagsPopover ? (
          <Popover
            trigger={['hover', 'focus', 'click']}
            placement="bottom"
            overlayClassName="manage-books-flags-overlay"
            content={
              <TranslationFlagsPopoverContent
                bookGuid={book.bookguid}
                flagsDisplay={flagsDisplay}
                normalizedTranslationProgress={normalizedTranslationProgress}
                onMarkLanguageComplete={onMarkLanguageComplete}
                onUnmarkLanguageComplete={onUnmarkLanguageComplete}
              />
            }
          >
            <button
              type="button"
              className={`manage-books-translation-summary is-interactive ${hasActiveTranslations ? 'is-active' : ''}`}
              aria-haspopup="dialog"
              aria-label={`Show translation flags for ${book.title || 'Untitled book'}: ${translationSummary}`}
              onClick={(event) => event.stopPropagation()}
            >
              {translationSummary}
            </button>
          </Popover>
        ) : statusUnavailable ? (
          <button
            type="button"
            className="manage-books-translation-summary is-error is-interactive"
            onClick={() => onRefreshProgress(book.bookguid)}
            aria-label={`Retry translation status for ${book.title || 'Untitled book'}`}
          >
            <ReloadOutlined />
            Status unavailable · Retry
          </button>
        ) : (
          <span
            className={`manage-books-translation-summary ${
              showFlagsLoading || isRefreshingProgress
                ? 'is-loading'
                : book.has_blocking_lock || book.needs_translation_refresh || book.flagStatus === 'error'
                ? 'is-error'
                : hasActiveTranslations
                ? 'is-active'
                : ''
            }`}
          >
            {(showFlagsLoading || isRefreshingProgress) && (
              <span className="manage-books-flag-spinner" aria-hidden="true" />
            )}
            {translationSummary}
          </span>
        )}
      </div>
      <div className="manage-books-list-actions">
        <Button
          size="small"
          type="primary"
          onClick={() => onTranslate(book.bookguid)}
          data-testid={`tutorial:manage-books:list-open:${book.bookguid}`}
        >
          Open
        </Button>
        <Dropdown
          trigger={['click']}
          menu={{
            items: moreActionItems,
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === 'refresh') onRefreshProgress(book.bookguid);
              if (key === 'replace') onReplace(book.bookguid);
              if (key === 'delete') onDelete(domEvent as React.MouseEvent<HTMLElement>, book.bookguid);
            },
          }}
        >
          <Button
            size="small"
            icon={<MoreOutlined />}
            aria-label={`More actions for ${book.title}`}
            data-testid={`tutorial:manage-books:list-more:${book.bookguid}`}
          >
            More
          </Button>
        </Dropdown>
      </div>
    </div>
  );
};

export const BookListView: React.FC<BookListViewProps> = ({
  books,
  ariaLabel = 'Books list',
  onDelete,
  onReplace,
  onTranslate,
  onSeriesManage,
  onRefreshProgress,
  allPossibleLanguages,
  showTranslatedFlags,
  flagsSettingEnabled,
  refreshingProgressIds,
  manualCompleteMap,
  onMarkLanguageComplete = () => {},
  onUnmarkLanguageComplete = () => {},
  onRequestDetails,
}) => {
  return (
    <div className="manage-books-list-view" role="table" aria-label={ariaLabel}>
      <div className="manage-books-list-header" role="row">
        <span>Book</span>
        <span>Series</span>
        <span>Language</span>
        <span>Status</span>
        <span>Actions</span>
      </div>
      {books.map(book => (
        <BookListRow
          key={book.bookguid}
          book={book}
          onDelete={onDelete}
          onReplace={onReplace}
          onTranslate={onTranslate}
          onSeriesManage={onSeriesManage}
          onRefreshProgress={onRefreshProgress}
          allPossibleLanguages={allPossibleLanguages}
          showTranslatedFlags={showTranslatedFlags}
          flagsSettingEnabled={flagsSettingEnabled}
          isRefreshingProgress={refreshingProgressIds.includes(book.bookguid)}
          manualCompletedLanguages={manualCompleteMap[book.bookguid] || []}
          onMarkLanguageComplete={onMarkLanguageComplete}
          onUnmarkLanguageComplete={onUnmarkLanguageComplete}
          onRequestDetails={onRequestDetails}
        />
      ))}
    </div>
  );
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState<T>(value);
  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

const enhanceBook = (book: IBook): BookRecord => ({
  ...book,
  _searchTitle: (book.title || '').toLowerCase(),
  _searchAuthor: (book.author || '').toLowerCase(),
  _searchSeries: normalizeLibrarySeriesName(book.series, '').toLowerCase(),
  detailsLoaded: book.detailsLoaded ?? book.progress_state === 'ready',
});

const sortBooks = (books: BookRecord[]): BookRecord[] =>
  [...books].sort((a, b) => {
    const authorCompare = (a.author || '').localeCompare(b.author || '');
    if (authorCompare !== 0) return authorCompare;

    const seriesA = normalizeLibrarySeriesName(a.series);
    const seriesB = normalizeLibrarySeriesName(b.series);
    const seriesCompare = seriesA.localeCompare(seriesB);
    if (seriesCompare !== 0) return seriesCompare;

    const numberA = a.seriesBookNumber || 0;
    const numberB = b.seriesBookNumber || 0;
    if (numberA !== numberB) return numberA - numberB;

    return (a.title || '').localeCompare(b.title || '');
  });

export type ManageBooksTestOverrides = {
  initialBooks?: IBook[];
  initialManualCompleteMap?: Record<string, string[]>;
  initialSeriesBootstrapStatus?: 'unchecked' | 'pending' | 'done';
  skipInitialFetch?: boolean;
  bookItemRenderer?: React.ComponentType<BookItemWithMenuProps>;
};

type BookManagementTabProps = {
  user: User | null;
  showLoginModal?: () => void;
  authResolved?: boolean;
  testOverrides?: ManageBooksTestOverrides;
};

// Main component
const BookManagementTab: React.FC<BookManagementTabProps> = ({
  user,
  showLoginModal,
  authResolved,
  testOverrides,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const tutorialSandbox = useTutorialSandbox();
  const tutorialModeRequested = isTutorialSandboxModeEnabled();
  const tutorialFixtureBooks = useMemo<IBook[] | null>(() => {
    if (!tutorialSandbox.enabled || tutorialSandbox.mode !== 'fixture') return null;
    const books = tutorialSandbox.fixturePayload?.books;
    return Array.isArray(books) ? (books as IBook[]) : null;
  }, [tutorialSandbox.enabled, tutorialSandbox.fixturePayload, tutorialSandbox.mode]);
  const tutorialRouteFixtureBooks = useMemo<IBook[] | null>(() => {
    if (!tutorialModeRequested) return null;
    const params = new URLSearchParams(location.search || '');
    const fixtureId = params.get('tutorial_fixture') || 'empty-library-user';
    return buildTutorialManageBooksFixture(fixtureId) as IBook[];
  }, [location.search, tutorialModeRequested]);
  const initialBooksOverride = testOverrides?.initialBooks ?? tutorialRouteFixtureBooks ?? tutorialFixtureBooks ?? undefined;
  const initialManualMapOverride = testOverrides?.initialManualCompleteMap;
  const skipInitialFetch = !!testOverrides?.skipInitialFetch || !!tutorialFixtureBooks;
  const [isLoading, setIsLoading] = useState(false);
  const [bookDetails, setBookDetails] = useState<BookRecord[]>(() =>
    initialBooksOverride ? initialBooksOverride.map(enhanceBook) : []
  );
  const detailsLoadedRef = useRef<Set<string>>(new Set());
  const detailsInFlightRef = useRef<Set<string>>(new Set());
  const pendingDetailIdsRef = useRef<Set<string>>(new Set());
  const detailFetchTimerRef = useRef<number | null>(null);
  const [recalculatingBookId, setRecalculatingBookId] = useState<string | null>(null);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isReplaceModalVisible, setIsReplaceModalVisible] = useState(false);
  const [replaceTargetBook, setReplaceTargetBook] = useState<BookRecord | null>(null);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replaceError, setReplaceError] = useState<string | null>(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const [replacePreviewRows, setReplacePreviewRows] = useState<ReplacePreviewRow[]>([]);
  const [replacePreviewToken, setReplacePreviewToken] = useState<string | null>(null);
  const [replaceChapterVersions, setReplaceChapterVersions] = useState<Record<string, string>>({});
  const [replaceSkippedChapters, setReplaceSkippedChapters] = useState<number[]>([]);
  const [isReplacePreviewing, setIsReplacePreviewing] = useState(false);
  const [replacePreviewError, setReplacePreviewError] = useState<string | null>(null);
  const [isCheckingReplaceLock, setIsCheckingReplaceLock] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const tutorialModeActive = tutorialSandbox.enabled || tutorialModeRequested;
  const pendingRedirectRef = useRef(false);
  const redirectTo = (location.state as { redirectTo?: string } | null)?.redirectTo;
  const pageDisabled = false;
  const disabledMessage = "After a lot of feedback, we're making improvements. Check back soon! -Team ScribeShadow";
  const [selectedAuthor, setSelectedAuthor] = useState<string | null>(null);
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
  const [selectedTranslationLanguage, setSelectedTranslationLanguage] = useState<string | null>(null);
  const [selectedTranslationStatus, setSelectedTranslationStatus] = useState<LibraryTranslationStatus | null>(null);
  const deferredTranslationLanguage = useDeferredValue(selectedTranslationLanguage);
  const deferredTranslationStatus = useDeferredValue(selectedTranslationStatus);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [viewMode, setViewMode] = useState<ManageBooksViewMode>(
    () => readStoredManageBooksViewMode(user?.uid) ?? 'covers'
  );
  const [sortMode, setSortMode] = useState<ManageBooksSortMode>(
    () => readStoredManageBooksSortMode(user?.uid) ?? 'author'
  );
  const [groupByMode, setGroupByMode] = useState<ManageBooksGroupByMode>(
    () => readStoredManageBooksGroupByMode(user?.uid) ?? DEFAULT_MANAGE_BOOKS_GROUP_BY_MODE
  );
  const [expandedAuthors, setExpandedAuthors] = useState<Record<string, boolean>>({});
  const [expandedSeries, setExpandedSeries] = useState<Record<string, boolean>>({});
  const [expandedLibraryGroups, setExpandedLibraryGroups] = useState<Record<string, boolean>>({});
  const [isBetaUser, setIsBetaUser] = useState(false);
  const [showTranslatedFlags, setShowTranslatedFlags] = useState<boolean>(true);
  const [simpleModeEnabled, setSimpleModeEnabled] = useState<boolean>(false);
  const [refreshingProgressIds, setRefreshingProgressIds] = useState<string[]>([]);
  const [seriesBootstrapStatus, setSeriesBootstrapStatus] = useState<'unchecked' | 'pending' | 'done'>(
    testOverrides?.initialSeriesBootstrapStatus ?? 'unchecked'
  );
  const seriesBootstrapInFlight = useRef(false);
  const seriesIntegrityChecked = useRef(false);
  const [isBootstrapLoading, setIsBootstrapLoading] = useState(false);
  const [loadingTip, setLoadingTip] = useState('Loading your books...');
  const uploadButtonRef = useRef<HTMLButtonElement | null>(null);
  const uploadModalRef = useRef<HTMLDivElement | null>(null);
  const replacePreviewRunIdRef = useRef(0);
  const [uploadTourOpen, setUploadTourOpen] = useState(false);
  const [uploadTourStep, setUploadTourStep] = useState(0);
  const [uploadTourEnabled, setUploadTourEnabled] = useState(false);
  const [uploadTourFlagsChecked, setUploadTourFlagsChecked] = useState(false);
  const [uploadTourManual, setUploadTourManual] = useState(false);
  const [uploadModalReady, setUploadModalReady] = useState(false);
  const [pendingUploadTourStep, setPendingUploadTourStep] = useState<number | null>(null);
  const tourFallbackRef = useRef<HTMLElement | null>(null);
  const savedViewModeRef = useRef<ManageBooksViewMode | null>(null);
  const savedSortModeRef = useRef<ManageBooksSortMode | null>(null);
  const savedGroupByModeRef = useRef<ManageBooksGroupByMode | null>(null);
  const viewModeRef = useRef<ManageBooksViewMode>('covers');
  const viewModeTouchedRef = useRef(false);
  const sortModeTouchedRef = useRef(false);
  const groupByModeTouchedRef = useRef(false);
  const viewPreferenceReadyRef = useRef(false);

  // Manual complete overrides: bookGuid -> [languages]
  type ManualMap = Record<string, string[]>;
  type DeleteModalState = {
    visible: boolean;
    targetBookGuid: string | null;
    targetTitle: string | null;
    isSubmitting: boolean;
  };

  const [manualCompleteMap, setManualCompleteMap] = useState<ManualMap>(() => {
    if (initialManualMapOverride) {
      return { ...initialManualMapOverride };
    }
    return {};
  });
  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    visible: false,
    targetBookGuid: null,
    targetTitle: null,
    isSubmitting: false,
  });
  const [progressRefreshPending, setProgressRefreshPending] = useState(false);
  const [progressRefreshAttempts, setProgressRefreshAttempts] = useState(0);

  useEffect(() => {
    if (!initialBooksOverride) return;
    setBookDetails(initialBooksOverride.map(enhanceBook));
    detailsLoadedRef.current = new Set(initialBooksOverride.map(book => book.bookguid));
    detailsInFlightRef.current = new Set();
    pendingDetailIdsRef.current = new Set();
  }, [initialBooksOverride]);

  useEffect(() => {
    if (!initialManualMapOverride) return;
    setManualCompleteMap({ ...initialManualMapOverride });
  }, [initialManualMapOverride]);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  const manualStorageKey = useMemo(() => user?.uid ? `poeai:manualComplete:${user.uid}` : null, [user?.uid]);
  const surveyRedirectActive = Boolean(redirectTo);

  useEffect(() => {
    if (!redirectTo) {
      pendingRedirectRef.current = false;
      return;
    }

    if (!authResolved) {
      return;
    }

    if (!user) {
      pendingRedirectRef.current = true;
      return;
    }

    pendingRedirectRef.current = false;
    navigate(redirectTo, { replace: true });
  }, [authResolved, navigate, redirectTo, showLoginModal, user]);

  useEffect(() => {
    if (!manualStorageKey) {
      setManualCompleteMap({});
      return;
    }
    try {
      const raw = localStorage.getItem(manualStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as ManualMap;
        setManualCompleteMap(parsed || {});
      } else {
        setManualCompleteMap({});
      }
    } catch {
      setManualCompleteMap({});
    }
  }, [manualStorageKey]);

  useEffect(() => {
    if (!manualStorageKey) return;
    try {
      localStorage.setItem(manualStorageKey, JSON.stringify(manualCompleteMap));
    } catch {}
  }, [manualStorageKey, manualCompleteMap]);

  const resolveTourTarget = useCallback(
    (element?: HTMLElement | null, fallbackToBody: boolean = true): HTMLElement => {
      if (element) {
        return element;
      }

      if (!fallbackToBody && uploadModalRef.current) {
        return uploadModalRef.current;
      }

      if (typeof document !== 'undefined' && document.body) {
        return document.body;
      }

      if (!tourFallbackRef.current) {
        tourFallbackRef.current = typeof document !== 'undefined'
          ? document.createElement('div')
          : ({} as HTMLElement);
      }

      return tourFallbackRef.current;
    },
    [setBookDetails]
  );

  const getTourElement = useCallback((id: string) => {
    if (typeof document === 'undefined') return null;
    return document.querySelector(`[data-tour-id="${id}"]`) as HTMLElement | null;
  }, []);

  useEffect(() => {
    if (tutorialModeActive) {
      setUploadTourEnabled(false);
      setUploadTourFlagsChecked(true);
      setSeriesBootstrapStatus('done');
      viewPreferenceReadyRef.current = false;
      savedViewModeRef.current = null;
      savedSortModeRef.current = null;
      savedGroupByModeRef.current = null;
      viewModeTouchedRef.current = false;
      sortModeTouchedRef.current = false;
      groupByModeTouchedRef.current = false;
      return;
    }
    if (!user) {
      setUploadTourEnabled(false);
      setUploadTourFlagsChecked(false);
      setSeriesBootstrapStatus('unchecked');
      setViewMode('covers');
      setSortMode('author');
      setGroupByMode(DEFAULT_MANAGE_BOOKS_GROUP_BY_MODE);
      viewPreferenceReadyRef.current = false;
      savedViewModeRef.current = null;
      savedSortModeRef.current = null;
      savedGroupByModeRef.current = null;
      viewModeTouchedRef.current = false;
      sortModeTouchedRef.current = false;
      groupByModeTouchedRef.current = false;
      return;
    }
    if (uploadTourFlagsChecked) return;

    const fetchFlags = async () => {
      try {
        const res = await axios.get(`${API_BASE}/user/ui-flags`, { params: { userId: user.uid } });
        const uiFlags = res.data?.uiFlags || {};
        const seen = !!uiFlags.manageBooksUploadTourSeen;
        if (!seen) {
          setUploadTourEnabled(true);
        }
        setSimpleModeEnabled(Boolean(uiFlags.simpleModeEnabled));
        const remoteViewMode = isManageBooksViewMode(uiFlags.manageBooksViewMode)
          ? uiFlags.manageBooksViewMode
          : null;
        const localViewMode = readStoredManageBooksViewMode(user.uid);
        const savedViewMode = remoteViewMode ?? localViewMode ?? 'covers';
        savedViewModeRef.current = remoteViewMode ?? (localViewMode ? null : savedViewMode);
        if (!viewModeTouchedRef.current) {
          setViewMode(savedViewMode);
        }
        const remoteSortMode = isManageBooksSortMode(uiFlags.manageBooksSortMode)
          ? uiFlags.manageBooksSortMode
          : null;
        const localSortMode = readStoredManageBooksSortMode(user.uid);
        const savedSortMode = remoteSortMode ?? localSortMode ?? 'author';
        savedSortModeRef.current = remoteSortMode ?? (localSortMode ? null : savedSortMode);
        if (!sortModeTouchedRef.current) {
          setSortMode(savedSortMode);
        }
        const remoteGroupByMode = isManageBooksGroupByMode(uiFlags.manageBooksGroupByMode)
          ? uiFlags.manageBooksGroupByMode
          : null;
        const localGroupByMode = readStoredManageBooksGroupByMode(user.uid);
        const savedGroupByMode = remoteGroupByMode ?? localGroupByMode ?? DEFAULT_MANAGE_BOOKS_GROUP_BY_MODE;
        savedGroupByModeRef.current = remoteGroupByMode ?? (localGroupByMode ? null : savedGroupByMode);
        if (!groupByModeTouchedRef.current) {
          setGroupByMode(savedGroupByMode);
        }
        const bootstrapDone = !!uiFlags.seriesBootstrapDone;
        setSeriesBootstrapStatus(bootstrapDone ? 'done' : 'pending');
      } catch (_e) {
        // non-blocking
        const fallbackViewMode = readStoredManageBooksViewMode(user.uid) ?? viewModeRef.current;
        const fallbackSortMode = readStoredManageBooksSortMode(user.uid) ?? sortMode;
        const fallbackGroupByMode = readStoredManageBooksGroupByMode(user.uid) ?? DEFAULT_MANAGE_BOOKS_GROUP_BY_MODE;
        savedViewModeRef.current = fallbackViewMode;
        savedSortModeRef.current = fallbackSortMode;
        savedGroupByModeRef.current = fallbackGroupByMode;
        if (!viewModeTouchedRef.current) {
          setViewMode(fallbackViewMode);
        }
        if (!sortModeTouchedRef.current) {
          setSortMode(fallbackSortMode);
        }
        if (!groupByModeTouchedRef.current) {
          setGroupByMode(fallbackGroupByMode);
        }
        setSeriesBootstrapStatus('pending');
      } finally {
        viewPreferenceReadyRef.current = true;
        setUploadTourFlagsChecked(true);
      }
    };
    fetchFlags();
  }, [groupByMode, sortMode, tutorialModeActive, user, uploadTourFlagsChecked]);

  const handleViewModeChange = useCallback((value: string | number) => {
    if (!isManageBooksViewMode(value)) {
      return;
    }
    viewModeTouchedRef.current = true;
    persistManageBooksViewMode(user?.uid, value);
    setViewMode(value);
  }, [user?.uid]);

  const handleSortModeChange = useCallback((value: string) => {
    if (!isManageBooksSortMode(value)) {
      return;
    }
    sortModeTouchedRef.current = true;
    persistManageBooksSortMode(user?.uid, value);
    setSortMode(value);
  }, [user?.uid]);

  const handleGroupByModeChange = useCallback((value: string) => {
    if (!isManageBooksGroupByMode(value)) {
      return;
    }
    groupByModeTouchedRef.current = true;
    persistManageBooksGroupByMode(user?.uid, value);
    setGroupByMode(value);
  }, [user?.uid]);

  useEffect(() => {
    if (tutorialModeActive || !user || !viewPreferenceReadyRef.current) return;
    persistManageBooksViewMode(user.uid, viewMode);
    persistManageBooksSortMode(user.uid, sortMode);
    persistManageBooksGroupByMode(user.uid, groupByMode);
    if (
      savedViewModeRef.current === viewMode &&
      savedSortModeRef.current === sortMode &&
      savedGroupByModeRef.current === groupByMode
    ) return;
    savedViewModeRef.current = viewMode;
    savedSortModeRef.current = sortMode;
    savedGroupByModeRef.current = groupByMode;
    axios.post(`${API_BASE}/user/ui-flags/set`, {
      userId: user.uid,
      flags: {
        manageBooksViewMode: viewMode,
        manageBooksSortMode: sortMode,
        manageBooksGroupByMode: groupByMode,
      },
    }).catch(() => {
      // Non-blocking preference save; keep the current UI state.
    });
  }, [API_BASE, groupByMode, sortMode, tutorialModeActive, user, viewMode]);

  const handleMarkLanguageComplete = useCallback(async (bookGuid: string, language: string) => {
    // Optimistic update
    setManualCompleteMap(prev => {
      const current = new Set(prev[bookGuid] || []);
      current.add(language);
      return { ...prev, [bookGuid]: Array.from(current) };
    });
    try {
      if (!user) throw new Error('Authentication required');
      const authHeaders = await getAuthHeaders(user);
      await axios.post(`${API_BASE}/uploadedbooks/${bookGuid}/manual_completed_languages`, {
        language,
        complete: true,
      }, {
        headers: authHeaders,
      });
      message.success(`Marked ${language} as complete`);
    } catch (e) {
      // Revert on failure
      setManualCompleteMap(prev => {
        const current = new Set(prev[bookGuid] || []);
        current.delete(language);
        return { ...prev, [bookGuid]: Array.from(current) };
      });
      message.error(`Failed to mark ${language} as complete`);
    }
  }, [user]);

  const handleUnmarkLanguageComplete = useCallback(async (bookGuid: string, language: string) => {
    // Optimistic update
    setManualCompleteMap(prev => {
      const current = new Set(prev[bookGuid] || []);
      current.delete(language);
      const next = { ...prev } as ManualMap;
      next[bookGuid] = Array.from(current);
      return next;
    });
    try {
      if (!user) throw new Error('Authentication required');
      const authHeaders = await getAuthHeaders(user);
      await axios.post(`${API_BASE}/uploadedbooks/${bookGuid}/manual_completed_languages`, {
        language,
        complete: false,
      }, {
        headers: authHeaders,
      });
      message.info(`Removed manual mark for ${language}`);
    } catch (e) {
      // Revert on failure
      setManualCompleteMap(prev => {
        const current = new Set(prev[bookGuid] || []);
        current.add(language);
        return { ...prev, [bookGuid]: Array.from(current) };
      });
      message.error(`Failed to remove manual mark for ${language}`);
    }
  }, [user]);

// Dynamically compute available languages based on beta status using centralized config
const allPossibleLanguages = useMemo(() => {
  const activeLanguages = getAllConfiguredLanguages(isBetaUser);
  // Always include legacy languages for display purposes
  return [...activeLanguages, ...legacyLanguages];
}, [isBetaUser]);

const translationLanguageOptions = useMemo(() => {
  const seen = new Set<string>();
  return allPossibleLanguages.filter(language => {
    const key = canonicalizeLanguageKey(language);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}, [allPossibleLanguages]);

  const uploadTourSteps = useMemo<TourProps['steps']>(() => {
    const getModalElement = () => {
      if (uploadModalRef.current) return uploadModalRef.current;
      if (typeof document !== 'undefined') {
        return document.querySelector('.ant-modal-content') as HTMLElement | null;
      }
      return null;
    };

    return [
      {
        title: 'Upload your first book',
        description: renderAppTourPanel(
          <>
            Click <strong>Upload Book</strong> to start your library.
            <AppTourVideoLink href={tutorialVideoLinks.manageBooksTour}>
              Watch the Manage Books tour
            </AppTourVideoLink>
          </>,
          { kicker: 'Library' }
        ),
        target: () => resolveTourTarget(uploadButtonRef.current) ?? undefined,
        placement: 'bottom',
      },
      {
        title: 'Everything lives here',
        description: renderAppTourPanel(
          <>
            This dialog collects the manuscript, identity, placement, and source language
            ScribeShadow needs before it can build the book workspace.
          </>,
          { kicker: 'Upload flow' }
        ),
        target: () => resolveTourTarget(getModalElement(), false) ?? undefined,
        placement: 'center',
      },
      {
        title: 'Upload resources',
        description: renderAppTourPanel(
          <>
            These links jump to our tutorial and formatting checklist. Keep them handy if you’re
            prepping a file for the first time.
            <AppTourVideoLink href={tutorialVideoLinks.uploadFirstBook}>
              Watch the upload tutorial
            </AppTourVideoLink>
          </>,
          { kicker: 'Prepare' }
        ),
        target: () => resolveTourTarget(getTourElement('upload-resources'), false) ?? undefined,
        placement: 'bottom',
      },
      {
        title: 'Choose the manuscript file',
        description: renderAppTourPanel(
          <>
            Drag in one <code>.docx</code> or <code>.epub</code> file under 30 MB. DOCX is the
            default for predictable editing; EPUB is best when the structure is final.
          </>,
          { kicker: 'File' }
        ),
        target: () => resolveTourTarget(getTourElement('upload-dropzone') ?? getTourElement('upload-file'), false) ?? undefined,
        placement: 'bottom',
      },
      {
        title: 'Add book identity',
        description: renderAppTourPanel(
          <>
            Title and author are required. They keep your library organized and carry through
            metadata, exports, and translated editions.
          </>,
          { kicker: 'Identity' }
        ),
        target: () => resolveTourTarget(getTourElement('upload-identity'), false) ?? undefined,
        placement: 'top',
      },
      {
        title: 'Set placement and language',
        description: renderAppTourPanel(
          <>
            Series details are optional, but the source language is required. Double-check it
            because translations use this language as the source text.
          </>,
          { kicker: 'Placement' }
        ),
        target: () => resolveTourTarget(getTourElement('upload-placement'), false) ?? undefined,
        placement: 'top',
      },
      {
        title: 'Set series order',
        description: renderAppTourPanel(
          <>
            Series number controls shelf order. Use whole numbers for the main sequence and decimals
            only when you need prequels, novellas, or side stories to sort between books.
          </>,
          { kicker: 'Series' }
        ),
        target: () => resolveTourTarget(getTourElement('upload-series-number') ?? getTourElement('upload-placement'), false) ?? undefined,
        placement: 'top',
      },
      {
        title: 'Submit when ready',
        description: renderAppTourPanel(
          <>
            The footer tells you what is missing. After upload, processing starts automatically and
            the new book appears in your library when ingestion finishes.
          </>,
          { kicker: 'Readiness' }
        ),
        target: () => resolveTourTarget(getTourElement('upload-submit-summary'), false) ?? undefined,
        placement: 'top',
      },
    ];
  }, [resolveTourTarget, getTourElement]);

  // Memoize handlers to prevent recreation on each render
  const ensureBookWordCounts = useCallback(async (book: IBook | undefined): Promise<boolean> => {
    if (!book) {
      return true;
    }
    if (!user) {
      return true;
    }
    if ((book.word_count_version ?? 0) >= WORD_COUNT_VERSION) {
      return true;
    }
    if (recalculatingBookId && recalculatingBookId !== book.bookguid) {
      message.warning('Please wait for the current word count update to finish.');
      return false;
    }

    const key = `recalc-${book.bookguid}`;
    setRecalculatingBookId(book.bookguid);
    message.loading({ content: 'Updating word counts...', key, duration: 0 });
    try {
      const response = await axios.post(`${API_BASE}/wordcount/recalculate-book`, {
        user_id: user.uid,
        book_id: book.bookguid,
        force: false,
      });
      const version = response.data?.book_version ?? WORD_COUNT_VERSION;
      setBookDetails(prev =>
        prev.map(existing =>
          existing.bookguid === book.bookguid
            ? { ...existing, word_count_version: version }
            : existing
        )
      );
      message.success({ content: 'Word counts refreshed.', key });
      return true;
    } catch (error) {
      console.error('Failed to refresh book word counts', error);
      message.error({ content: 'Failed to refresh word counts. Please try again.', key, duration: 4 });
      return false;
    } finally {
      setRecalculatingBookId(null);
    }
  }, [user, recalculatingBookId, setBookDetails, setRecalculatingBookId]);

  const notifyStandaloneSeries = useCallback((text: string) => {
    message.info(text);
  }, []);

  const {
    goToMarketing: handleMarketingClick,
    goToAudiobook: handleAudiobookClick,
    goToSeriesManage: handleSeriesManageClick,
  } = useMemo(
    () =>
      createBookNavigationHandlers({
        navigate,
        notifyStandaloneSeries,
        noSeriesLabel: NO_SERIES_LABEL,
      }),
    [navigate, notifyStandaloneSeries]
  );

  const handleTranslateClick = useCallback((bookGuid: string) => {
    const run = async () => {
      const target = bookDetails.find(b => b.bookguid === bookGuid);
      const ready = await ensureBookWordCounts(target);
      if (!ready) {
        return;
      }
      navigate(`/book-detail/${bookGuid}`);
    };
    void run();
  }, [bookDetails, ensureBookWordCounts, navigate]);  

  const processedBooks = useMemo<ProcessedBook[]>(() => {
    if (bookDetails.length === 0) return [];
    const mapped = bookDetails.map(book => {
      const normalizedSeries = normalizeLibrarySeriesName(book.series);
      const titleLower = (book.title || '').toLowerCase();
      const authorLower = (book.author || '').toLowerCase();
      const seriesLower = normalizedSeries.toLowerCase();
      return {
        book,
        titleLower,
        authorLower,
        seriesLower,
        searchValue: `${titleLower} ${authorLower} ${seriesLower}`,
      };
    });
    return mapped.sort((a, b) => {
      const seriesA = normalizeLibrarySeriesName(a.book.series);
      const seriesB = normalizeLibrarySeriesName(b.book.series);
      const numberA = a.book.seriesBookNumber || 0;
      const numberB = b.book.seriesBookNumber || 0;
      const titleCompare = (a.book.title || '').localeCompare(b.book.title || '');
      const authorCompare = (a.book.author || '').localeCompare(b.book.author || '');
      const seriesCompare = seriesA.localeCompare(seriesB);

      if (sortMode === 'title') {
        return titleCompare || authorCompare || seriesCompare || numberA - numberB;
      }
      if (sortMode === 'series') {
        return seriesCompare || numberA - numberB || titleCompare || authorCompare;
      }
      return authorCompare || seriesCompare || numberA - numberB || titleCompare;
    });
  }, [bookDetails, sortMode]);

  const libraryIndex = useMemo(() => {
    const authors = new Set<string>();
    const series = new Set<string>();
    const authorToSeriesMap = new Map<string, Set<string>>();
    const seriesToAuthorsMap = new Map<string, Set<string>>();

    processedBooks.forEach(({ book }) => {
      const author = book.author;
      const rawSeries = normalizeLibrarySeriesName(book.series, '');

      authors.add(author);

      let authorSeries = authorToSeriesMap.get(author);
      if (!authorSeries) {
        authorSeries = new Set<string>();
        authorToSeriesMap.set(author, authorSeries);
      }

      if (rawSeries) {
        authorSeries.add(rawSeries);
        series.add(rawSeries);

        let authorsForSeries = seriesToAuthorsMap.get(rawSeries);
        if (!authorsForSeries) {
          authorsForSeries = new Set<string>();
          seriesToAuthorsMap.set(rawSeries, authorsForSeries);
        }
        authorsForSeries.add(author);
      }
    });

    return {
      authors: Array.from(authors).sort((a, b) => a.localeCompare(b)),
      series: Array.from(series).sort((a, b) => a.localeCompare(b)),
      authorToSeries: authorToSeriesMap,
      seriesToAuthors: seriesToAuthorsMap,
    };
  }, [processedBooks]);

  const uniqueAuthors = libraryIndex.authors;
  const uniqueSeries = libraryIndex.series;
  const { authorToSeries, seriesToAuthors } = libraryIndex;

  const filteredAuthors = useMemo<string[]>(() => {
    if (!selectedSeries) return uniqueAuthors;
    const authorsForSeries = seriesToAuthors.get(selectedSeries);
    if (!authorsForSeries) return [];
    return Array.from(authorsForSeries).sort((a, b) => a.localeCompare(b));
  }, [selectedSeries, seriesToAuthors, uniqueAuthors]);

  const filteredSeries = useMemo<string[]>(() => {
    if (!selectedAuthor) return uniqueSeries;
    const seriesForAuthor = authorToSeries.get(selectedAuthor);
    if (!seriesForAuthor || seriesForAuthor.size === 0) return [];
    return Array.from(seriesForAuthor).sort((a, b) => a.localeCompare(b));
  }, [selectedAuthor, authorToSeries, uniqueSeries]);

  const debouncedSearchInput = useDebouncedValue(searchInput, 350);
  const deferredSearchInput = useDeferredValue(debouncedSearchInput);
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const searchTerms = useMemo<string[]>(() => {
    if (!normalizedQuery) return [];
    return normalizedQuery.split(/\s+/).filter(Boolean);
  }, [normalizedQuery]);

  const authorSeriesGroups = useMemo<AuthorSeriesGroup[]>(() => {
    if (processedBooks.length === 0) return [];

    const groups: AuthorSeriesGroup[] = [];
    let currentAuthor: string | null = null;
    let currentSeriesName: string | null = null;
    let currentGroup: AuthorSeriesGroup | null = null;
    let currentSeriesGroup: Series | null = null;

    const matchesQuery = (entry: ProcessedBook) => {
      if (searchTerms.length === 0) return true;
      const { searchValue } = entry;
      for (let i = 0; i < searchTerms.length; i += 1) {
        if (!searchValue.includes(searchTerms[i])) {
          return false;
        }
      }
      return true;
    };

    processedBooks.forEach(entry => {
      const { book } = entry;
      if (selectedAuthor && book.author !== selectedAuthor) {
        return;
      }

      const seriesName = normalizeLibrarySeriesName(book.series);
      if (selectedSeries && seriesName !== selectedSeries) {
        return;
      }

      if (!bookMatchesLanguageStatus(
        book,
        deferredTranslationLanguage,
        deferredTranslationStatus,
        manualCompleteMap[book.bookguid] || book.manual_completed_languages || [],
      )) {
        return;
      }

      if (!matchesQuery(entry)) {
        return;
      }

      if (book.author !== currentAuthor) {
        currentAuthor = book.author;
        currentGroup = { author: book.author, series: [] };
        groups.push(currentGroup);
        currentSeriesName = null;
        currentSeriesGroup = null;
      }

      if (!currentGroup) return;

      if (seriesName !== currentSeriesName) {
        currentSeriesName = seriesName;
        currentSeriesGroup = { name: seriesName, books: [] };
        currentGroup.series.push(currentSeriesGroup);
      }

      currentSeriesGroup?.books.push(book);
    });

    return groups;
  }, [deferredTranslationLanguage, deferredTranslationStatus, manualCompleteMap, processedBooks, searchTerms, selectedAuthor, selectedSeries]);

  const resetReplacePreviewState = useCallback(() => {
    replacePreviewRunIdRef.current += 1;
    setReplacePreviewRows([]);
    setReplacePreviewToken(null);
    setReplaceChapterVersions({});
    setReplaceSkippedChapters([]);
    setReplacePreviewError(null);
    setIsReplacePreviewing(false);
  }, []);

  const runReplacePreview = useCallback(async (file: File, target: BookRecord) => {
    if (!user) {
      message.error('Please sign in before replacing the manuscript.', 5);
      return;
    }

    const runId = replacePreviewRunIdRef.current + 1;
    replacePreviewRunIdRef.current = runId;
    setIsReplacePreviewing(true);
    setReplacePreviewError(null);
    setReplacePreviewRows([]);
    setReplaceSkippedChapters([]);
    setReplacePreviewToken(null);
    setReplaceChapterVersions({});

    try {
      const authHeaders = await getAuthHeaders(user);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', 'replace');
      formData.append('user_id', user.uid);
      formData.append('full_replace', 'true');

      const response = await axios.post(`${API_BASE}/uploadedbooks/${target.bookguid}/chapter-upload/preview`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        timeout: 1000 * 60 * 5,
      });

      if (replacePreviewRunIdRef.current !== runId) {
        return;
      }
      const rows: ReplacePreviewRow[] = response.data?.chapters || [];
      const hasChapters = rows.length > 0;
      setReplacePreviewRows(hasChapters ? rows : []);
      setReplaceSkippedChapters(response.data?.skipped_chapters || []);
      setReplaceChapterVersions(response.data?.chapter_versions || {});
      const token = response.data?.preview_token || null;
      setReplacePreviewToken(hasChapters ? token : null);
      if (!hasChapters) {
        const msg = 'No chapters were detected in this upload.';
        setReplacePreviewError(msg);
        setReplaceFile(null);
        message.warning(msg, 5);
      }
    } catch (error: any) {
      if (replacePreviewRunIdRef.current !== runId) {
        return;
      }
      const errMsg = error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Unable to parse the upload.';
      setReplacePreviewError(errMsg);
      setReplacePreviewToken(null);
      message.error(errMsg, 6);
    } finally {
      if (replacePreviewRunIdRef.current === runId) {
        setIsReplacePreviewing(false);
      }
    }
  }, [user]);

  const closeReplaceModal = useCallback(() => {
    if (isReplacing) {
      return;
    }
    setIsReplaceModalVisible(false);
    setReplaceTargetBook(null);
    setReplaceFile(null);
    setReplaceError(null);
    resetReplacePreviewState();
    setIsCheckingReplaceLock(false);
  }, [isReplacing, resetReplacePreviewState]);

  const replaceUploadFileList = useMemo<UploadFile[]>(() => {
    if (!replaceFile) {
      return [];
    }
    return [
      {
        uid: replaceFile.name,
        name: replaceFile.name,
        status: 'done',
        size: replaceFile.size,
        type: replaceFile.type,
      }
    ];
  }, [replaceFile]);

  const handleReplaceBeforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>((file) => {
    const validation = validateReplaceFileSelection({
      fileName: file?.name || '',
      hasTarget: !!replaceTargetBook,
    });
    if (!validation.allowed) {
      if (validation.applyFieldError) {
        setReplaceError(validation.message);
      }
      const toastFn = validation.toastLevel === 'warning' ? message.warning : message.error;
      toastFn(validation.message, validation.toastDuration);
      return Upload.LIST_IGNORE;
    }

    setReplaceError(null);
    resetReplacePreviewState();
    setReplaceFile(file as File);
    if (replaceTargetBook) {
      void runReplacePreview(file as File, replaceTargetBook);
    }
    return false;
  }, [replaceTargetBook, resetReplacePreviewState, runReplacePreview]);

  const resetReplaceSelection = useCallback(() => {
    if (isReplacing) {
      return false;
    }
    setReplaceFile(null);
    setReplaceError(null);
    resetReplacePreviewState();
    return true;
  }, [isReplacing, resetReplacePreviewState]);

  const handleReplaceRemove = useCallback<NonNullable<UploadProps['onRemove']>>(() => {
    return resetReplaceSelection();
  }, [resetReplaceSelection]);

  const handleRefreshProgress = useCallback(
    async (bookGuid: string) => {
      setRefreshingProgressIds(prev => [...prev, bookGuid]);
      setBookDetails(prev => prev.map(book => book.bookguid === bookGuid
        ? { ...book, progress_state: 'refreshing', needs_translation_refresh: false }
        : book));
      const key = `refresh-${bookGuid}`;
      message.loading({ content: 'Refreshing progress...', key });

      try {
        if (!user) throw new Error('Authentication required');
        const authHeaders = await getAuthHeaders(user);
        const response = await axios.post<Record<string, TranslationProgressEntry>>(
          `${API_BASE}/uploadedbooks/${bookGuid}/refresh_progress`,
          {},
          { headers: authHeaders },
        );

        const updatedProgress = response.data;
        setBookDetails(prev =>
          prev.map(bk =>
            bk.bookguid === bookGuid
              ? {
                  ...bk,
                  translation_progress: updatedProgress,
                  progress_state: 'ready',
                  flagStatus: 'ready',
                  detailsLoaded: true,
                  needs_translation_refresh: false,
                }
              : bk
          )
        );
        message.success({ content: 'Progress refreshed', key, duration: 2 });
      } catch (error) {
        console.error('Error refreshing progress:', error);
        setBookDetails(prev => prev.map(book => book.bookguid === bookGuid
          ? {
              ...book,
              progress_state: 'unavailable',
              flagStatus: 'error',
              detailsLoaded: true,
              needs_translation_refresh: false,
            }
          : book));
        message.error({ content: 'Failed to refresh progress', key, duration: 2 });
      } finally {
        // remove spinner state
        setRefreshingProgressIds(prev => prev.filter(id => id !== bookGuid));
      }
    },
    [user]
  );


  const toggleAuthorExpansion = useCallback((author: string) => {
    setExpandedAuthors(prev => ({
      ...prev,
      [author]: !prev[author]
    }));
  }, []);

  const toggleSeriesExpansion = useCallback((author: string, series: string) => {
    const key = `${author}-${series}`;
    setExpandedSeries(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const toggleLibraryGroupExpansion = useCallback((key: string) => {
    setExpandedLibraryGroups(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const [, startExpandTransition] = useTransition();

const collapseAll = useCallback((): void => {
  startExpandTransition(() => {
    setExpandedAuthors(prev => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach(k => { next[k] = false; });
      return next;
    });
    setExpandedSeries(prev => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach(k => { next[k] = false; });
      return next;
    });
    setExpandedLibraryGroups(prev => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach(k => { next[k] = false; });
      return next;
    });
  });
}, []);

const expandAll = useCallback((): void => {
  startExpandTransition(() => {
    const nextAuthors: Record<string, boolean> = {};
    const nextSeries: Record<string, boolean> = {};
    authorSeriesGroups.forEach(group => {
      nextAuthors[group.author] = true;
      group.series.forEach(s => {
        nextSeries[`${group.author}-${s.name}`] = true;
      });
    });
    setExpandedAuthors(nextAuthors);
    setExpandedSeries(nextSeries);
    setExpandedLibraryGroups(prev => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach(k => { next[k] = true; });
      return next;
    });
  });
}, [authorSeriesGroups]);

const anyExpanded = useMemo<boolean>(() => {
  return (
    Object.values(expandedAuthors).some(v => v) ||
    Object.values(expandedSeries).some(v => v) ||
    Object.values(expandedLibraryGroups).some(v => v)
  );
}, [expandedAuthors, expandedLibraryGroups, expandedSeries]);

const toggleAll = useCallback((): void => {
  if (anyExpanded) collapseAll(); else expandAll();
}, [anyExpanded, collapseAll, expandAll]);

  useEffect(() => {
    if (authorSeriesGroups.length === 0) {
      setExpandedAuthors({});
      setExpandedSeries({});
      return;
    }

    setExpandedAuthors(prev => {
      const next: Record<string, boolean> = {};
      authorSeriesGroups.forEach(group => {
        if (typeof prev[group.author] === 'boolean') {
          next[group.author] = prev[group.author];
        } else {
          next[group.author] = true;
        }
      });
      return next;
    });

    setExpandedSeries(prev => {
      const next: Record<string, boolean> = {};
      authorSeriesGroups.forEach(group => {
        group.series.forEach(serie => {
          const key = `${group.author}-${serie.name}`;
          if (typeof prev[key] === 'boolean') {
            next[key] = prev[key];
          } else {
            next[key] = true;
          }
        });
      });
      return next;
    });
  }, [authorSeriesGroups]);

  useEffect(() => {
    if (tutorialModeActive) {
      setIsBetaUser(false);
      setShowTranslatedFlags(true);
      setIsLoading(false);
      return;
    }
    // Check if the user is part of the beta program
    if (!user) {
      if (!tutorialModeActive) {
        setBookDetails([]);
        console.error("User is not authenticated");
      }
      setIsLoading(false);
      return;
    }
    const userId = user.uid; 
	const checkBetaStatus = async () => {
      if (userId) {
        try {
          const response = await axios.get(`${API_BASE}/check-beta-status/${userId}`);
          setIsBetaUser(response.data.isBetaUser);
		  setShowTranslatedFlags(response.data.showTranslatedFlags ?? true);
        } catch (error) {
          console.error('Error checking beta status:', error);
        }
      }
    };
	
    checkBetaStatus();
  }, [tutorialModeActive, user]);	  
  
  const handleModalOpen = useCallback(() => {
    setIsUploadModalVisible(true);
    setFormKey(prevKey => prevKey + 1);
    setUploadModalReady(false);
    setPendingUploadTourStep(null);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!tutorialModeActive || !isTutorialUiSuppressionRequested()) return;

    const bridge = () => {
      if (!isUploadModalVisible) {
        setUploadModalReady(false);
        setFormKey(prevKey => prevKey + 1);
        setPendingUploadTourStep(null);
      }
      setIsUploadModalVisible(true);
      return true;
    };

    (window as any).__tutorialOpenUploadDialog = bridge;
    return () => {
      if ((window as any).__tutorialOpenUploadDialog === bridge) {
        delete (window as any).__tutorialOpenUploadDialog;
      }
    };
  }, [tutorialModeActive, isUploadModalVisible]);

  const handleModalClose = useCallback(() => {
    setIsUploadModalVisible(false);
    setFormKey(prevKey => prevKey + 1);
    setUploadModalReady(false);
    setPendingUploadTourStep(null);
  }, []);

  const handleUploadTourChange = useCallback(
    (nextStep: number) => {
      if (tutorialModeActive) {
        return;
      }
      if (nextStep <= 0) {
        setPendingUploadTourStep(null);
        setUploadTourStep(0);
        if (isUploadModalVisible) {
          setIsUploadModalVisible(false);
          setUploadModalReady(false);
        }
        return;
      }

      if (!isUploadModalVisible) {
        setIsUploadModalVisible(true);
        setFormKey(prevKey => prevKey + 1);
        setUploadModalReady(false);
      }

      if (!uploadModalReady) {
        setPendingUploadTourStep(nextStep);
        return;
      }

      setPendingUploadTourStep(null);
      setUploadTourStep(nextStep);
    },
    [tutorialModeActive, isUploadModalVisible, uploadModalReady]
  );

  const handleUploadTourClose = useCallback(async () => {
    setUploadTourOpen(false);
    setUploadTourStep(0);
    setUploadTourEnabled(false);
    setUploadTourManual(false);
    setPendingUploadTourStep(null);
    if (!user) return;
    try {
      await axios.post(`${API_BASE}/user/ui-flags/set`, {
        userId: user.uid,
        flags: { manageBooksUploadTourSeen: true },
      });
    } catch (_e) {
      // Non-blocking — failure just means we may show the tour again later.
    }
  }, [user]);

  const handleStartUploadTour = useCallback(() => {
    setUploadTourEnabled(true);
    setUploadTourFlagsChecked(true);
    setUploadTourStep(0);
    setUploadTourOpen(true);
    setIsUploadModalVisible(false);
    setUploadTourManual(true);
    setUploadModalReady(false);
    setPendingUploadTourStep(null);
  }, []);

const [, startTransition] = useTransition();

  const showErrorToast = useCallback((text: string) => {
    message.error(text);
  }, []);

  const fetchBookDetails = useCallback(
    async (bookIds: string[], options?: { force?: boolean }): Promise<IBook[]> => {
      if (!user) return [];
      const uniqueIds = Array.from(new Set(bookIds)).filter(Boolean).slice(0, 50);
      const pending = uniqueIds.filter(
        id => (options?.force || !detailsLoadedRef.current.has(id)) && !detailsInFlightRef.current.has(id)
      );
      if (pending.length === 0) return [];

      pending.forEach(id => detailsInFlightRef.current.add(id));
      try {
        const authHeaders = await getAuthHeaders(user);
        const response = await axios.post<IBook[]>(`${API_BASE}/uploadedbooks/details`, {
          user_id: user.uid,
          book_ids: pending,
        }, {
          headers: authHeaders,
        });
        const details = response.data || [];
        const detailsById = new Map(details.map(item => [item.bookguid, item]));

        setBookDetails(prev =>
          prev.map(existing => {
            const detail = detailsById.get(existing.bookguid);
            if (!detail) {
              if (!pending.includes(existing.bookguid)) return existing;
              return {
                ...existing,
                flagStatus: 'error',
                progress_state: 'unavailable',
                detailsLoaded: true,
                needs_translation_refresh: false,
              };
            }
            const merged = { ...existing, ...detail };
            merged.flagStatus = detail.progress_state === 'refreshing' ? 'loading' : 'ready';
            merged.detailsLoaded = true;
            return merged;
          })
        );

        details.forEach(item => detailsLoadedRef.current.add(item.bookguid));
        pending.forEach(id => {
          if (!detailsById.has(id)) {
            detailsLoadedRef.current.add(id);
          }
        });
        return details;
      } catch (error) {
        console.error('Failed to fetch book details', error);
        setBookDetails(prev =>
          prev.map(existing =>
            pending.includes(existing.bookguid)
              ? options?.force
                ? {
                    ...existing,
                    flagStatus: 'loading',
                    progress_state: 'refreshing',
                    detailsLoaded: true,
                    needs_translation_refresh: true,
                  }
                : {
                  ...existing,
                  flagStatus: 'error',
                  progress_state: 'unavailable',
                  detailsLoaded: true,
                  needs_translation_refresh: false,
                }
              : existing
          )
        );
        pending.forEach(id => detailsLoadedRef.current.add(id));
        return [];
      } finally {
        pending.forEach(id => detailsInFlightRef.current.delete(id));
      }
    },
    [user]
  );

  const handleReplaceRequest = useCallback((bookGuid: string) => {
    const target = bookDetails.find(b => b.bookguid === bookGuid) || null;
    if (!target) {
      message.error('Unable to find that book. Please refresh and try again.');
      return;
    }
    setReplaceTargetBook(target);
    setReplaceFile(null);
    setReplaceError(null);
    resetReplacePreviewState();
    setIsReplaceModalVisible(true);
  }, [bookDetails, resetReplacePreviewState]);

  useEffect(() => {
    if (!isReplaceModalVisible) return;
    if (!replaceTargetBook) return;
    if (!user) return;

    let cancelled = false;
    let timer: number | null = null;
    const run = async () => {
      const shouldRecheck = replaceTargetBook.has_blocking_lock === true;
      if (replaceTargetBook.has_blocking_lock === false) {
        setIsCheckingReplaceLock(false);
        setReplaceError(null);
        return;
      }
      setIsCheckingReplaceLock(true);
      try {
        const authHeaders = await getAuthHeaders(user).catch(() => ({} as Record<string, string>));
        const details = await axios.post(
          `${API_BASE}/uploadedbooks/${replaceTargetBook.bookguid}/locks`,
          { user_id: user.uid },
          { headers: authHeaders }
        );
        if (cancelled) return;
        const lockData = details?.data || {};
        setReplaceTargetBook(prev => (prev ? { ...prev, ...lockData } : prev));
        if (lockData?.has_blocking_lock) {
          setReplaceError(
            'This book is busy finishing a translation or proofread. Please wait for it to finish before replacing the manuscript.'
          );
        } else {
          setReplaceError(null);
        }
        if (lockData?.has_blocking_lock && shouldRecheck && !cancelled) {
          timer = window.setTimeout(() => {
            if (!cancelled) {
              void run();
            }
          }, 30000);
        }
      } catch (error) {
        if (!cancelled) {
          setReplaceError('We could not confirm the book status. Please try again in a moment.');
        }
      } finally {
        if (!cancelled) {
          setIsCheckingReplaceLock(false);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [isReplaceModalVisible, replaceTargetBook, user]);

  const queueBookDetails = useCallback(
    (bookIds: string[]) => {
      if (!user) return;
      const pendingSet = pendingDetailIdsRef.current;
      bookIds.forEach(id => {
        if (!id) return;
        if (detailsLoadedRef.current.has(id)) return;
        if (detailsInFlightRef.current.has(id)) return;
        pendingSet.add(id);
      });
      if (pendingSet.size === 0) return;
      if (detailFetchTimerRef.current !== null) {
        window.clearTimeout(detailFetchTimerRef.current);
      }
      detailFetchTimerRef.current = window.setTimeout(() => {
        detailFetchTimerRef.current = null;
        const queued = Array.from(pendingSet);
        pendingSet.clear();
        const batches: string[][] = [];
        for (let index = 0; index < queued.length; index += 50) {
          batches.push(queued.slice(index, index + 50));
        }
        void (async () => {
          for (const batch of batches) {
            await fetchBookDetails(batch);
          }
        })();
      }, 150);
    },
    [fetchBookDetails, user]
  );

  const fetchBooks = useMemo(
    () =>
      createFetchBooks({
        user,
        startTransition,
        setBookDetails,
        setManualCompleteMap,
        setLoadingTip,
        setIsLoading,
        messageError: showErrorToast,
        onBooksLoaded: () => {
          detailsLoadedRef.current = new Set();
          detailsInFlightRef.current = new Set();
          pendingDetailIdsRef.current = new Set();
          if (detailFetchTimerRef.current !== null) {
            window.clearTimeout(detailFetchTimerRef.current);
            detailFetchTimerRef.current = null;
          }
        },
        makeRequest: async () => {
          const authHeaders = await getAuthHeaders(user);
          return requestWithRetry(
            () => axios.get<IBook[]>(`${API_BASE}/uploadedbooks/user/${user?.uid}/index`, { headers: authHeaders }),
            {
              maxAttempts: 4,
              initialDelayMs: 600,
              maxDelayMs: 5000,
              retryableStatusCodes: [408, 425, 429, 500, 502, 503, 504],
            }
          );
        },
      }),
    [user, startTransition, setBookDetails, setManualCompleteMap, setLoadingTip, setIsLoading, showErrorToast]
  );

  const bootstrapSeriesForUser = useCallback(async () => {
    if (tutorialModeActive) return;
    await runSeriesBootstrap({
      apiBase: API_BASE,
      user,
      books: bookDetails,
      axiosInstance: axios,
      getAuthHeadersFn: getAuthHeaders,
      noSeriesLabel: NO_SERIES_LABEL,
      seriesBootstrapInFlight,
      setLoadingTip,
      setIsBootstrapLoading,
      setSeriesBootstrapStatus,
      fetchBooks,
    });
  }, [
    API_BASE,
    bookDetails,
    fetchBooks,
    tutorialModeActive,
    user,
    setLoadingTip,
    setIsBootstrapLoading,
    setSeriesBootstrapStatus,
  ]);

  useEffect(() => {
    const runIntegrityCheck = async () => {
      if (tutorialModeActive) return;
      if (!user) return;
      if (seriesBootstrapInFlight.current) return;
      if (!bookDetails || bookDetails.length === 0) return;
      if (seriesBootstrapStatus === 'done') {
        seriesIntegrityChecked.current = true;
        return;
      }
      if (seriesIntegrityChecked.current) return;

      try {
        const headers = await getAuthHeaders(user).catch(() => ({} as Record<string, string>));
        const res = await axios.get<SeriesSummary[]>(`${API_BASE}/series`, {
          params: { user_id: user.uid },
          headers,
        });
        const existingIds = new Set((res.data || []).map(s => s.seriesId));
        const hasOrphanedSeriesId = bookDetails.some(b => b.seriesId && !existingIds.has(b.seriesId));
        if (hasOrphanedSeriesId) {
          setLoadingTip('Repairing missing series links...');
          setIsBootstrapLoading(true);
          setSeriesBootstrapStatus('pending');
          seriesBootstrapInFlight.current = false; // allow rerun immediately
          bootstrapSeriesForUser();
        }
      } catch (error) {
        console.warn('Series integrity check failed', error);
      } finally {
        seriesIntegrityChecked.current = true;
      }
    };
    runIntegrityCheck();
  }, [API_BASE, bookDetails, bootstrapSeriesForUser, seriesBootstrapStatus, tutorialModeActive, user]);

  useEffect(() => {
    if (deferredSearchInput === searchQuery) return;
    startTransition(() => {
      setSearchQuery(deferredSearchInput);
    });
  }, [deferredSearchInput, searchQuery, startTransition]);

  useEffect(() => {
    if (tutorialModeActive) {
      setProgressRefreshPending(false);
      setProgressRefreshAttempts(0);
      return;
    }
    const pending = bookDetails.some(book => book.needs_translation_refresh);
    setProgressRefreshPending(pending);
    if (!pending) {
      setProgressRefreshAttempts(0);
    }
  }, [tutorialModeActive, bookDetails]);

  useEffect(() => {
    if (tutorialModeActive) return;
    if (!progressRefreshPending) return;
    if (progressRefreshAttempts >= PROGRESS_REFRESH_MAX_ATTEMPTS) {
      setBookDetails(prev => prev.map(book => book.needs_translation_refresh
        ? {
            ...book,
            progress_state: 'unavailable',
            flagStatus: 'error',
            detailsLoaded: true,
            needs_translation_refresh: false,
          }
        : book));
      return;
    }
    if (isLoading) return;
    if (!user) return;

    const timer = window.setTimeout(() => {
      const pendingIds = bookDetails
        .filter(book => book.needs_translation_refresh)
        .map(book => book.bookguid);
      void fetchBookDetails(pendingIds, { force: true });
      setProgressRefreshAttempts(prev => prev + 1);
    }, PROGRESS_REFRESH_POLL_INTERVAL_MS);

    return () => window.clearTimeout(timer);
  }, [bookDetails, fetchBookDetails, tutorialModeActive, progressRefreshPending, progressRefreshAttempts, isLoading, user]);

  const handleReplaceSubmit = useCallback(async () => {
    const validation = validateReplaceSubmission({
      user,
      replaceTargetBook,
      replaceFile,
      isReplacePreviewing,
      replacePreviewToken,
      replacePreviewRows,
    });
    if (!validation.valid) {
      if (validation.replaceError) {
        setReplaceError(validation.replaceError);
      }
      if (validation.toast) {
        const toastFn =
          validation.toast.level === 'warning'
            ? message.warning
            : validation.toast.level === 'info'
            ? message.info
            : message.error;
        toastFn(validation.toast.message, validation.toast.duration);
      }
      return;
    }

    const ensuredUser = user;
    const ensuredFile = replaceFile;
    const ensuredTargetBook = replaceTargetBook;
    if (!ensuredUser || !ensuredFile || !ensuredTargetBook) {
      const fallbackMsg = 'Missing data required to replace the manuscript. Please try again.';
      setReplaceError(fallbackMsg);
      message.error(fallbackMsg, 6);
      return;
    }

    setIsLoading(true);
    setIsReplacing(true);
    setReplaceError(null);

    try {
      const authHeaders = await getAuthHeaders(ensuredUser);
      const formData = new FormData();
      formData.append('file', ensuredFile);
      formData.append('user_id', ensuredUser.uid);
      const payload: Record<string, any> = {
        previewToken: replacePreviewToken,
        expectedVersions: replaceChapterVersions,
      };
      formData.append('payload', JSON.stringify(payload));

      await axios.post(`${API_BASE}/uploadedbooks/${ensuredTargetBook.bookguid}/replace`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        timeout: 1000 * 60 * 10,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      message.success('Manuscript replaced successfully');
      setIsReplaceModalVisible(false);
      setReplaceTargetBook(null);
      setReplaceFile(null);
      resetReplacePreviewState();

      try {
        await fetchBooks({ rethrow: true, suppressErrorToast: true });
      } catch (refreshError) {
        console.error('Failed to refresh books after replacement:', refreshError);
        message.warning('Manuscript replaced, but we could not refresh your library. Please reload the page to see the latest list.', 6);
      }
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Failed to replace manuscript';
      setReplaceError(errMsg);
      message.error(errMsg, 6);
    } finally {
      setIsLoading(false);
      setIsReplacing(false);
    }
  }, [
    user,
    replaceFile,
    replaceTargetBook,
    replacePreviewToken,
    replaceChapterVersions,
    replacePreviewRows,
    isReplacePreviewing,
    fetchBooks,
    resetReplacePreviewState,
  ]);


  
 
 
useEffect(() => {
  if (tutorialModeActive) {
    const fixtureBooks = initialBooksOverride || [];
    setBookDetails(fixtureBooks.map(enhanceBook));
    detailsLoadedRef.current = new Set(fixtureBooks.map(book => book.bookguid));
    detailsInFlightRef.current = new Set();
    pendingDetailIdsRef.current = new Set();
    setIsLoading(false);
    return;
  }
  if (skipInitialFetch) {
    if (!user) {
      if (!tutorialModeActive) {
        setBookDetails([]);
      }
    }
    setIsLoading(false);
    return;
  }
  if (!user) {
    if (!tutorialModeActive) {
      setBookDetails([]);
    }
    setIsLoading(false);
    return;
  }
  fetchBooks();
}, [tutorialModeActive, user, fetchBooks, skipInitialFetch, initialBooksOverride]);

  useEffect(() => {
    if (tutorialModeActive) return;
    const shouldOpen = shouldAutoOpenUploadTour({
      uploadTourEnabled,
      uploadTourFlagsChecked,
      uploadTourOpen,
      hasUser: !!user,
      isLoading,
      pageDisabled,
      hasBooks: bookDetails.length > 0,
    });
    if (!shouldOpen) return;
    setUploadTourManual(false);
    setUploadTourStep(0);
    setUploadTourOpen(true);
    setIsUploadModalVisible(false);
    setUploadModalReady(false);
    setPendingUploadTourStep(null);
  }, [tutorialModeActive, uploadTourEnabled, uploadTourFlagsChecked, uploadTourOpen, user, isLoading, pageDisabled, bookDetails]);

  useEffect(() => {
    if (tutorialModeActive) return;
    const shouldClose = shouldAutoCloseUploadTour({
      uploadTourOpen,
      hasBooks: bookDetails.length > 0,
      uploadTourManual,
    });
    if (!shouldClose) return;
    handleUploadTourClose();
  }, [tutorialModeActive, bookDetails, uploadTourOpen, uploadTourManual, handleUploadTourClose]);

  useEffect(() => {
    if (tutorialModeActive) return;
    const shouldRun = shouldRunSeriesBootstrap({
      hasUser: !!user,
      seriesBootstrapStatus,
      seriesBootstrapInFlight: seriesBootstrapInFlight.current,
      hasBooks: !!bookDetails && bookDetails.length > 0,
    });
    if (!shouldRun) return;
    bootstrapSeriesForUser();
  }, [tutorialModeActive, bootstrapSeriesForUser, bookDetails, seriesBootstrapStatus, user]);

  const deleteBook = useCallback((event: React.MouseEvent<HTMLElement> | undefined, bookGuid: string) => {
    event?.stopPropagation?.();
    const targetBook = bookDetails.find(b => b.bookguid === bookGuid);
    setDeleteModalState({
      visible: true,
      targetBookGuid: bookGuid,
      targetTitle: targetBook?.title || 'Untitled book',
      isSubmitting: false,
    });
  }, [bookDetails]);

  const handleDeleteModalCancel = useCallback(() => {
    setDeleteModalState(prev => {
      if (prev.isSubmitting) {
        return prev;
      }
      return {
        visible: false,
        targetBookGuid: null,
        targetTitle: null,
        isSubmitting: false,
      };
    });
  }, []);

  const performDeleteBook = useCallback(async () => {
    const targetGuid = deleteModalState.targetBookGuid;
    if (!targetGuid) {
      return;
    }
    if (!user) {
      console.error("User is not authenticated");
      return;
    }

    setDeleteModalState(prev => ({ ...prev, isSubmitting: true }));

    try {
      await axios.delete(`${API_BASE}/uploadedbooks/${targetGuid}`, { data: { user_id: user.uid } });
      startTransition(() => {
        setBookDetails(prev => prev.filter(b => b.bookguid !== targetGuid));
      });
      message.success('Book deleted successfully');
      setDeleteModalState({
        visible: false,
        targetBookGuid: null,
        targetTitle: null,
        isSubmitting: false,
      });
    } catch (error) {
      console.error("Error deleting book: ", error);
      message.error('Failed to delete book');
      setDeleteModalState(prev => ({ ...prev, isSubmitting: false }));
    }
  }, [deleteModalState.targetBookGuid, user, startTransition]);

  const confirmDeleteBook = useCallback(() => {
    void performDeleteBook();
  }, [performDeleteBook]);

  const handleUpload = useCallback(async (formData: FormData) => {
    if (!user) {
      console.error("User is not authenticated");
      throw new Error("User is not authenticated");
    }

    setLoadingTip('Uploading your book...');
    setIsLoading(true);
    formData.append('user_id', user.uid);

    try {
      const authHeaders = await getAuthHeaders(user);
      //console.log("Uploading Book");
      const response = await axios.post(`${API_BASE}/upload-book/`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        timeout: 1000 * 60 * 10,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,		
      });

      if (response.data.status === 'error') {
        throw new Error(response.data.message);
      }

      // Close the modal
      handleModalClose();

      // Navigate to the book detail page using the bookguid
      const newBookGuid = response.data.book_id;
      navigate(`/book-detail/${newBookGuid}`);
      
    } catch (error) {
      console.error("Error uploading file: ", error);
      throw error; // Propagate the error to be caught in BookUploadForm
    } finally {
      setIsLoading(false);
    }
  }, [handleModalClose, navigate, user]);

  const handleNewBookPreflight = useCallback(async (file: File): Promise<NewBookPreflightResult> => {
    if (!user) {
      throw new Error('User is not authenticated');
    }

    const authHeaders = await getAuthHeaders(user);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.uid);

    const response = await axios.post(`${API_BASE}/upload-book/preview`, formData, {
      headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      timeout: 1000 * 60 * 5,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });

    const rawStatus = response.data?.status;
    return {
      previewToken: response.data?.preview_token || null,
      detectedSectionCount:
        typeof response.data?.detected_section_count === 'number'
          ? response.data.detected_section_count
          : null,
      status:
        rawStatus === 'ready' || rawStatus === 'needs_formatting'
          ? rawStatus
          : 'analysis_unavailable',
      reasonCodes: Array.isArray(response.data?.reason_codes)
        ? response.data.reason_codes
        : [],
    };
  }, [user]);



  const handleAuthorChange = useCallback((value: string | null) => {
    setSelectedAuthor(value);
    if (!value) {
      setSelectedSeries(null);
    }
  }, []);

  const handleSeriesChange = useCallback((value: string | null) => {
    setSelectedSeries(value);
    if (!value) {
      setSelectedAuthor(null);
    }
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedAuthor(null);
    setSelectedSeries(null);
    setSelectedTranslationLanguage(null);
    setSelectedTranslationStatus(null);
    setSearchInput('');
    startTransition(() => {
      setSearchQuery('');
    });
  }, [startTransition]);

  // Memoize filteredGroups to prevent recalculation on every render
  const filteredGroups = useMemo(() => {
    return authorSeriesGroups.filter(group => {
      if (selectedAuthor && group.author !== selectedAuthor) {
        return false;
      }
      if (selectedSeries) {
        return group.series.some(serie => serie.name === selectedSeries);
      }
      return true;
    });
  }, [authorSeriesGroups, selectedAuthor, selectedSeries]);

  const visibleBookCount = useMemo(
    () => filteredGroups.reduce(
      (groupTotal, group) =>
        groupTotal + group.series.reduce((seriesTotal, serie) => seriesTotal + serie.books.length, 0),
      0
    ),
    [filteredGroups]
  );
  const updatingTranslationStatusCount = useMemo(() => {
    if (!selectedTranslationLanguage) return 0;
    const targetKey = canonicalizeLanguageKey(selectedTranslationLanguage);
    return bookDetails.filter(book => {
      if (!book.needs_translation_refresh) return false;
      if (canonicalizeLanguageKey(book.language) === targetKey) return false;
      return (book.translation_pairs || []).some(
        pair => canonicalizeLanguageKey(getTargetLanguageFromPair(pair)) === targetKey
      );
    }).length;
  }, [bookDetails, selectedTranslationLanguage]);
  const flatVisibleBooks = useMemo(
    () => filteredGroups.flatMap(group => group.series.flatMap(serie => serie.books)),
    [filteredGroups]
  );
  const libraryBookGroups = useMemo(
    () => (groupByMode === 'author' || groupByMode === 'series')
      ? buildLibraryBookGroups(flatVisibleBooks, groupByMode)
      : [],
    [flatVisibleBooks, groupByMode]
  );
  const activeFilterCount = [
    selectedAuthor,
    selectedSeries,
    selectedTranslationLanguage,
    selectedTranslationStatus,
    searchQuery.trim() || searchInput.trim(),
  ].filter(Boolean).length;
  const libraryViewMenuItems = useMemo(() => [
    { key: 'view:covers', label: `${viewMode === 'covers' ? 'Selected: ' : ''}Cards view` },
    { key: 'view:list', label: `${viewMode === 'list' ? 'Selected: ' : ''}List view` },
    { type: 'divider' as const },
    { key: 'sort:author', label: `${sortMode === 'author' ? 'Selected: ' : ''}Sort by author` },
    { key: 'sort:title', label: `${sortMode === 'title' ? 'Selected: ' : ''}Sort by title` },
    { key: 'sort:series', label: `${sortMode === 'series' ? 'Selected: ' : ''}Sort by series` },
    { type: 'divider' as const },
    { key: 'group:none', label: `${groupByMode === 'none' ? 'Selected: ' : ''}No grouping` },
    { key: 'group:author_series', label: `${groupByMode === 'author_series' ? 'Selected: ' : ''}Group by author > series` },
    { key: 'group:author', label: `${groupByMode === 'author' ? 'Selected: ' : ''}Group by author` },
    { key: 'group:series', label: `${groupByMode === 'series' ? 'Selected: ' : ''}Group by series` },
  ], [groupByMode, sortMode, viewMode]);
  const handleLibraryViewMenuClick = useCallback((info: { key: string }) => {
    const [scope, value] = info.key.split(':');
    if (scope === 'view') {
      handleViewModeChange(value);
      return;
    }
    if (scope === 'sort') {
      handleSortModeChange(value);
      return;
    }
    if (scope === 'group') {
      handleGroupByModeChange(value);
    }
  }, [handleGroupByModeChange, handleSortModeChange, handleViewModeChange]);

  // Suppress heavy UI sections while typing and memoize the large groups render
  const isSearching = searchInput !== searchQuery;
  const effectiveShowTranslatedFlags = showTranslatedFlags && !isSearching;

  const BookCardComponent = testOverrides?.bookItemRenderer || BookItemWithMenu;

  useEffect(() => {
    if (groupByMode === 'none' || libraryBookGroups.length === 0) {
      setExpandedLibraryGroups({});
      return;
    }

    setExpandedLibraryGroups(prev => {
      const next: Record<string, boolean> = {};
      libraryBookGroups.forEach(group => {
        next[group.key] = typeof prev[group.key] === 'boolean' ? prev[group.key] : true;
      });
      return next;
    });
  }, [groupByMode, libraryBookGroups]);

  const renderBookCard = useCallback((book: BookRecord) => (
    <BookCardComponent
      key={book.bookguid}
      book={book}
      onDelete={deleteBook}
      onReplace={handleReplaceRequest}
      onMarketing={handleMarketingClick}
      onAudio={handleAudiobookClick}
      onTranslate={handleTranslateClick}
      isRecalculating={recalculatingBookId === book.bookguid}
      onRefreshProgress={handleRefreshProgress}
      allPossibleLanguages={allPossibleLanguages}
      showTranslatedFlags={effectiveShowTranslatedFlags}
      flagsSettingEnabled={true}
      isRefreshingProgress={refreshingProgressIds.includes(book.bookguid)}
      manualCompletedLanguages={manualCompleteMap[book.bookguid] || []}
      onMarkLanguageComplete={handleMarkLanguageComplete}
      onUnmarkLanguageComplete={handleUnmarkLanguageComplete}
      onRequestDetails={(bookGuid) => queueBookDetails([bookGuid])}
    />
  ), [
    BookCardComponent,
    allPossibleLanguages,
    deleteBook,
    effectiveShowTranslatedFlags,
    handleAudiobookClick,
    handleMarkLanguageComplete,
    handleMarketingClick,
    handleRefreshProgress,
    handleReplaceRequest,
    handleTranslateClick,
    handleUnmarkLanguageComplete,
    manualCompleteMap,
    queueBookDetails,
    recalculatingBookId,
    refreshingProgressIds
  ]);

  const renderBookList = useCallback((books: BookRecord[], ariaLabel = 'Books list') => (
    <BookListView
      books={books}
      ariaLabel={ariaLabel}
      onDelete={deleteBook}
      onReplace={handleReplaceRequest}
      onSeriesManage={handleSeriesManageClick}
      onTranslate={handleTranslateClick}
      onRefreshProgress={handleRefreshProgress}
      allPossibleLanguages={allPossibleLanguages}
      showTranslatedFlags={effectiveShowTranslatedFlags}
      flagsSettingEnabled={true}
      refreshingProgressIds={refreshingProgressIds}
      manualCompleteMap={manualCompleteMap}
      onMarkLanguageComplete={handleMarkLanguageComplete}
      onUnmarkLanguageComplete={handleUnmarkLanguageComplete}
      onRequestDetails={(bookGuid) => queueBookDetails([bookGuid])}
    />
  ), [
    allPossibleLanguages,
    deleteBook,
    effectiveShowTranslatedFlags,
    handleMarkLanguageComplete,
    handleRefreshProgress,
    handleReplaceRequest,
    handleSeriesManageClick,
    handleTranslateClick,
    handleUnmarkLanguageComplete,
    manualCompleteMap,
    queueBookDetails,
    refreshingProgressIds
  ]);

  const groupsContent = useMemo(() => (
    <>
      {filteredGroups.map(group => {
        const visibleSeries = group.series.filter(serie => !selectedSeries || serie.name === selectedSeries);
        const bookCount = visibleSeries.reduce((count, series) => count + series.books.length, 0);
        const singleStandaloneSeries = visibleSeries.length === 1 && visibleSeries[0].name === NO_SERIES_LABEL;
        return (
          <AuthorBooksContainer
            key={group.author}
            className={`manage-books-author-section ${bookCount <= 2 ? 'is-compact' : ''}`}
          >
            <AuthorHeader
              className="manage-books-author-header"
              onClick={() => toggleAuthorExpansion(group.author)}
              role="button"
              tabIndex={0}
              aria-expanded={!!expandedAuthors[group.author]}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleAuthorExpansion(group.author);
                }
              }}
            >
              <CollapseIcon>
                {expandedAuthors[group.author]
                  ? <CaretDownOutlined />
                  : <CaretRightOutlined />
                }
              </CollapseIcon>
              <span className="manage-books-author-title">{group.author}</span>
              {singleStandaloneSeries && <span className="manage-books-inline-label">Standalone</span>}
              <BookCountBadge>{bookCount} books</BookCountBadge>
            </AuthorHeader>

            {expandedAuthors[group.author] && singleStandaloneSeries && (
              <SeriesContent className="manage-books-series-section manage-books-standalone-direct">
                <VirtualBookGrid
                  books={visibleSeries[0].books}
                  maxInitialVisible={24}
                  renderBook={renderBookCard}
                />
              </SeriesContent>
            )}

            {expandedAuthors[group.author] && !singleStandaloneSeries && visibleSeries
              .map(serie => {
              const seriesKey = `${group.author}-${serie.name}`;
              return (
                <SeriesContent key={serie.name} className="manage-books-series-section">
                  <SeriesHeader
                    className="manage-books-series-header"
                    onClick={() => toggleSeriesExpansion(group.author, serie.name)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={!!expandedSeries[seriesKey]}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleSeriesExpansion(group.author, serie.name);
                      }
                    }}
                  >
                    <div className="manage-books-series-header-main">
                      <CollapseIcon>
                        {expandedSeries[seriesKey] 
                          ? <CaretDownOutlined /> 
                          : <CaretRightOutlined />
                        }
                      </CollapseIcon>
                      <span className="manage-books-series-title">
                        {serie.name === NO_SERIES_LABEL ? "Standalone Books" : serie.name}
                      </span>
                      {serie.books.length > 1 && 
                        <BookCountBadge>{serie.books.length} books</BookCountBadge>
                      }
                      {serie.name !== NO_SERIES_LABEL && (
                        <Tooltip title="Edit series-wide metadata">
                          <Button
                            type="text"
                            size="small"
                            icon={<FormOutlined />}
                            className="manage-books-series-edit-action"
                            aria-label={`Edit ${serie.name} series metadata`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSeriesManageClick(
                                serie.name,
                                serie.books.find(book => book.seriesId)?.seriesId
                              );
                            }}
                          >
                            Edit series
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </SeriesHeader>

                  {expandedSeries[seriesKey] && (
                    <VirtualBookGrid
                      books={serie.books}
                      maxInitialVisible={24}
                      renderBook={renderBookCard}
                    />
                  )}
                </SeriesContent>
              );
            })}
          </AuthorBooksContainer>
        );
      })}
    </>
  ), [
    filteredGroups,
    expandedAuthors,
    expandedSeries,
    selectedSeries,
    toggleAuthorExpansion,
    toggleSeriesExpansion,
    renderBookCard
  ]);

  const flatCoverContent = useMemo(() => (
    <VirtualBookGrid
      books={flatVisibleBooks}
      maxInitialVisible={36}
      renderBook={renderBookCard}
      fillViewport
      forceVirtualize
      centerItems
    />
  ), [flatVisibleBooks, renderBookCard]);

  const authorSeriesListContent = useMemo(() => (
    <>
      {filteredGroups.map(group => {
        const visibleSeries = group.series.filter(serie => !selectedSeries || serie.name === selectedSeries);
        const bookCount = visibleSeries.reduce((count, series) => count + series.books.length, 0);
        const singleStandaloneSeries = visibleSeries.length === 1 && visibleSeries[0].name === NO_SERIES_LABEL;
        return (
          <AuthorBooksContainer
            key={group.author}
            className="manage-books-author-section manage-books-author-series-list-section"
          >
            <AuthorHeader
              className="manage-books-author-header"
              onClick={() => toggleAuthorExpansion(group.author)}
              role="button"
              tabIndex={0}
              aria-expanded={!!expandedAuthors[group.author]}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleAuthorExpansion(group.author);
                }
              }}
            >
              <CollapseIcon>
                {expandedAuthors[group.author]
                  ? <CaretDownOutlined />
                  : <CaretRightOutlined />
                }
              </CollapseIcon>
              <span className="manage-books-author-title">{group.author}</span>
              {singleStandaloneSeries && <span className="manage-books-inline-label">Standalone</span>}
              <BookCountBadge>{bookCount} books</BookCountBadge>
            </AuthorHeader>

            {expandedAuthors[group.author] && singleStandaloneSeries && (
              <SeriesContent className="manage-books-series-section manage-books-standalone-direct">
                {renderBookList(visibleSeries[0].books, `${group.author} books list`)}
              </SeriesContent>
            )}

            {expandedAuthors[group.author] && !singleStandaloneSeries && visibleSeries.map(serie => {
              const seriesKey = `${group.author}-${serie.name}`;
              return (
                <SeriesContent key={serie.name} className="manage-books-series-section">
                  <SeriesHeader
                    className="manage-books-series-header"
                    onClick={() => toggleSeriesExpansion(group.author, serie.name)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={!!expandedSeries[seriesKey]}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleSeriesExpansion(group.author, serie.name);
                      }
                    }}
                  >
                    <div className="manage-books-series-header-main">
                      <CollapseIcon>
                        {expandedSeries[seriesKey]
                          ? <CaretDownOutlined />
                          : <CaretRightOutlined />
                        }
                      </CollapseIcon>
                      <span className="manage-books-series-title">
                        {serie.name === NO_SERIES_LABEL ? 'Standalone Books' : serie.name}
                      </span>
                      <BookCountBadge>{getBookCountLabel(serie.books.length)}</BookCountBadge>
                      {serie.name !== NO_SERIES_LABEL && (
                        <Tooltip title="Edit series-wide metadata">
                          <Button
                            type="text"
                            size="small"
                            icon={<FormOutlined />}
                            className="manage-books-series-edit-action"
                            aria-label={`Edit ${serie.name} series metadata`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSeriesManageClick(
                                serie.name,
                                serie.books.find(book => book.seriesId)?.seriesId
                              );
                            }}
                          >
                            Edit series
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </SeriesHeader>

                  {expandedSeries[seriesKey] && renderBookList(serie.books, `${group.author} ${serie.name} books list`)}
                </SeriesContent>
              );
            })}
          </AuthorBooksContainer>
        );
      })}
    </>
  ), [
    expandedAuthors,
    expandedSeries,
    filteredGroups,
    handleSeriesManageClick,
    renderBookList,
    selectedSeries,
    toggleAuthorExpansion,
    toggleSeriesExpansion,
  ]);

  const groupedListContent = useMemo(() => (
    <div className="manage-books-grouped-list">
      {libraryBookGroups.map(group => {
        const isExpanded = expandedLibraryGroups[group.key] !== false;
        return (
          <AuthorBooksContainer
            key={group.key}
            className="manage-books-group-section manage-books-list-group-section"
          >
            <AuthorHeader
              className="manage-books-group-header"
              onClick={() => toggleLibraryGroupExpansion(group.key)}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleLibraryGroupExpansion(group.key);
                }
              }}
            >
              <CollapseIcon>
                {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
              </CollapseIcon>
              <span className="manage-books-group-title">{group.label}</span>
              <BookCountBadge>{getBookCountLabel(group.books.length)}</BookCountBadge>
            </AuthorHeader>
            {isExpanded && (
              <div className="manage-books-list-group-content">
                {renderBookList(group.books, `${group.label} books list`)}
              </div>
            )}
          </AuthorBooksContainer>
        );
      })}
    </div>
  ), [expandedLibraryGroups, libraryBookGroups, renderBookList, toggleLibraryGroupExpansion]);

  const groupedCoverContent = useMemo(() => (
    <>
      {libraryBookGroups.map(group => {
        const isExpanded = expandedLibraryGroups[group.key] !== false;
        return (
          <AuthorBooksContainer
            key={group.key}
            className={`manage-books-group-section ${group.books.length <= 2 ? 'is-compact' : ''}`}
          >
            <AuthorHeader
              className="manage-books-group-header"
              onClick={() => toggleLibraryGroupExpansion(group.key)}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleLibraryGroupExpansion(group.key);
                }
              }}
            >
              <CollapseIcon>
                {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
              </CollapseIcon>
              <span className="manage-books-group-title">{group.label}</span>
              <BookCountBadge>{getBookCountLabel(group.books.length)}</BookCountBadge>
            </AuthorHeader>
            {isExpanded && (
              <SeriesContent className="manage-books-series-section manage-books-group-grid-section">
                <VirtualBookGrid
                  books={group.books}
                  maxInitialVisible={24}
                  renderBook={renderBookCard}
                />
              </SeriesContent>
            )}
          </AuthorBooksContainer>
        );
      })}
    </>
  ), [expandedLibraryGroups, libraryBookGroups, renderBookCard, toggleLibraryGroupExpansion]);

  if (surveyRedirectActive && !authResolved) {
    return (
      <Container className="manage-books-shell">
        <StyledCard className="manage-books-workspace">
          <Space direction="vertical" align="center" className="manage-books-state-stack">
            <LoadingCard label="Checking your account..." />
          </Space>
        </StyledCard>
      </Container>
    );
  }

  if (surveyRedirectActive && authResolved && !user) {
    return (
      <Container className="manage-books-shell">
        <StyledCard className="manage-books-workspace">
          <Space direction="vertical" align="center" className="manage-books-state-stack">
            <p className="manage-books-state-copy">
              {(redirectTo === '/deepdive' || redirectTo === '/marketing') ? 'Sign in to continue to Deep Dive.' : 'Sign in to continue to your language survey.'}
            </p>
            <Button type="primary" className="app-primary-cta" size="large" onClick={() => showLoginModal && showLoginModal()}>
              Sign in
            </Button>
          </Space>
        </StyledCard>
      </Container>
    );
  }

  const isBootstrapPendingActive = seriesBootstrapStatus === 'pending' && bookDetails.length > 0;
  const showBlockingSpinner = isLoading || isBootstrapLoading || isBootstrapPendingActive;
  const blockingTip = !isBootstrapLoading && isBootstrapPendingActive
    ? 'Linking books to series (one-time setup)...'
    : loadingTip;

  return (
    <Container className="manage-books-shell">
      <div className="manage-books-inner">
        {!isLoading && user ? (
          <AppPageHeader
            className="manage-books-header"
            variant="banner"
            title="Manage Books"
            titleId="manage-books-title"
            subtitle="Upload manuscripts, review translation readiness, and manage book-level actions from one place."
            data-testid="tutorial:manage-books:header"
            actions={(
              <button
                type="button"
                className="manage-books-tour-link app-tour-trigger"
                onClick={handleStartUploadTour}
                disabled={uploadTourOpen}
              >
                <QuestionCircleOutlined />
                <span>Take a tour</span>
              </button>
            )}
          />
        ) : null}
      <StyledCard className="manage-books-workspace">
        {showBlockingSpinner && (
          <BlockingOverlay>
            <BlockingSpinnerContent>
              <Spin size="large" />
              <span>{blockingTip}</span>
            </BlockingSpinnerContent>
          </BlockingOverlay>
        )}
        
  {!isLoading && user && !user?.emailVerified ? (
          <EmptyState className="manage-books-state-panel">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Please verify your email to access this page."
            />
            <Button type="primary">Resend Verification Email</Button>
          </EmptyState>
        ) : (
          <>    
          {!isLoading && pageDisabled ? (
            <EmptyState className="manage-books-state-panel">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={disabledMessage}
              />
            </EmptyState>
          ) : (
            <>
            {!isLoading && user ? (
              <>
                <FilterContainer className="manage-books-toolbar app-toolbar-row" data-testid="tutorial:manage-books:toolbar">
                  <div className="manage-books-toolbar-layout">
                    <div className="manage-books-toolbar-row">
                      <div className="manage-books-toolbar-filters">
                        <FilterSection className="manage-books-filter-section manage-books-search-section" data-testid="tutorial:manage-books:search-section">
                          <Input
                            placeholder="Search books"
                            allowClear
                            className="manage-books-search"
                            aria-label="Search books"
                            data-testid="tutorial:manage-books:search"
                            value={searchInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSearchInput(value);
                              if (value === '') {
                                startTransition(() => setSearchQuery(''));
                              }
                            }}
                          />
                        </FilterSection>

                        <FilterSection className="manage-books-filter-section">
                          <Select
                            className="manage-books-filter-control"
                            placeholder="Author"
                            aria-label="Filter by Author"
                            data-testid="tutorial:manage-books:author-filter"
                            onChange={handleAuthorChange}
                            value={selectedAuthor}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                          >
                            {filteredAuthors.map(author => (
                              <Option key={author} value={author}>{author}</Option>
                            ))}
                          </Select>
                        </FilterSection>

                        <FilterSection className="manage-books-filter-section">
                          <Select
                            className="manage-books-filter-control"
                            placeholder="Series"
                            aria-label="Filter by Series"
                            data-testid="tutorial:manage-books:series-filter"
                            onChange={handleSeriesChange}
                            value={selectedSeries}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                          >
                            {filteredSeries.map(series => (
                              <Option key={series} value={series}>{series}</Option>
                            ))}
                          </Select>
                        </FilterSection>

                        <span
                          className="manage-books-count-text"
                          aria-live="polite"
                          aria-label={`${visibleBookCount.toLocaleString()} ${visibleBookCount === 1 ? 'book' : 'books'} shown${activeFilterCount > 0 ? `, ${activeFilterCount} active ${activeFilterCount === 1 ? 'filter' : 'filters'}` : ''}`}
                          title={activeFilterCount > 0 ? `${activeFilterCount} active ${activeFilterCount === 1 ? 'filter' : 'filters'}` : undefined}
                        >
                          {visibleBookCount.toLocaleString()}{visibleBookCount === 1 ? ' book' : ' books'}
                        </span>
                      </div>

                      <FilterActions className="manage-books-toolbar-actions">
                        <Popover
                          trigger="click"
                          placement="bottomLeft"
                          content={(
                            <div className="manage-books-translation-filter-popover">
                              <label className="manage-books-popover-filter-label">Language</label>
                              <Select
                                className="manage-books-popover-filter-control"
                                placeholder="Choose language"
                                aria-label="Filter by Translation language"
                                onChange={(value: string | null) => {
                                  setSelectedTranslationLanguage(value || null);
                                  if (!value) setSelectedTranslationStatus(null);
                                }}
                                value={selectedTranslationLanguage}
                                allowClear
                                showSearch
                                optionFilterProp="children"
                              >
                                {translationLanguageOptions.map(language => (
                                  <Option key={language} value={language}>{language}</Option>
                                ))}
                              </Select>

                              <label className="manage-books-popover-filter-label">Status</label>
                              <Select
                                className="manage-books-popover-filter-control"
                                placeholder="Translated books"
                                aria-label="Filter by Translation status"
                                onChange={(value: LibraryTranslationStatus | null) => setSelectedTranslationStatus(value || null)}
                                value={selectedTranslationStatus}
                                allowClear
                                disabled={!selectedTranslationLanguage}
                              >
                                <Option value="complete">Complete</Option>
                                <Option value="in_progress">In progress</Option>
                                <Option value="not_started">Not started</Option>
                                <Option value="unavailable">Status unavailable</Option>
                              </Select>
                            </div>
                          )}
                        >
                          <Button
                            icon={<TranslationOutlined />}
                            loading={updatingTranslationStatusCount > 0}
                            className={`manage-books-translation-filter-button${selectedTranslationLanguage ? ' is-active' : ''}`}
                            aria-label={selectedTranslationLanguage
                              ? `Filter translations, selected language ${selectedTranslationLanguage}${updatingTranslationStatusCount > 0 ? ', status updating' : ''}`
                              : 'Filter translations'}
                          >
                            Translations
                            {selectedTranslationLanguage && <span className="manage-books-filter-active-dot" aria-hidden="true" />}
                          </Button>
                        </Popover>
                        <Dropdown
                          trigger={['click']}
                          menu={{ items: libraryViewMenuItems, onClick: handleLibraryViewMenuClick }}
                        >
                          <Button icon={<UnorderedListOutlined />} data-testid="tutorial:manage-books:view-options">View options</Button>
                        </Dropdown>
                        {activeFilterCount > 0 && (
                          <Button type="link" className="manage-books-clear-filter-button" icon={<ClearOutlined />} onClick={clearFilters} data-testid="tutorial:manage-books:clear-filters">
                            Clear
                          </Button>
                        )}
                        <Tooltip title="Upload a new book">
                          <Button
                            ref={uploadButtonRef}
                            type="primary"
                            className="app-primary-cta"
                            icon={<UploadOutlined />}
                            onClick={handleModalOpen}
                            size="large"
                            data-testid="tutorial:upload-book:open-upload-dialog"
                          >
                            Upload Book
                          </Button>
                        </Tooltip>
                      </FilterActions>
                    </div>
                  </div>
                </FilterContainer>

                <Modal
                  title={
                    <div className="manage-books-modal-title-block">
                      <span className="manage-books-modal-kicker">Delete book</span>
                      <ModalTitle>{deleteModalState.targetTitle || 'Untitled book'}</ModalTitle>
                    </div>
                  }
                  visible={deleteModalState.visible}
                  onCancel={handleDeleteModalCancel}
                  footer={
                    <div className="manage-books-delete-modal-footer">
                      <div className="manage-books-delete-footer-actions">
                        <Button onClick={handleDeleteModalCancel} disabled={deleteModalState.isSubmitting}>
                          Keep book
                        </Button>
                        <Button
                          type="primary"
                          danger
                          icon={<DeleteOutlined />}
                          loading={deleteModalState.isSubmitting}
                          onClick={confirmDeleteBook}
                        >
                          Delete book
                        </Button>
                      </div>
                    </div>
                  }
                  maskClosable={!deleteModalState.isSubmitting}
                  closable={!deleteModalState.isSubmitting}
                  keyboard={!deleteModalState.isSubmitting}
                  destroyOnClose={false}
                  centered
                  width={560}
                  className="manage-books-modal manage-books-delete-modal"
                >
                  <ModalBody className="manage-books-modal-body">
                    {deleteModalState.isSubmitting && (
                      <ModalSpinnerOverlay>
                        <LoadingCard label="Deleting book… hang tight." />
                      </ModalSpinnerOverlay>
                    )}
                    <div className="manage-books-modal-stack manage-books-delete-modal-body">
                      <section className="translation-modal-block manage-books-delete-checklist" aria-labelledby="manage-books-delete-checklist-heading">
                        <div className="translation-modal-block-header">
                          <div>
                            <h3 id="manage-books-delete-checklist-heading">Before you delete</h3>
                            <p>The manuscript, translations, proofread chapters, and related book data will be removed.</p>
                          </div>
                        </div>
                        <div className="manage-books-delete-warning">
                          <ExclamationCircleFilled />
                          <span>Deleting this book is final. There is no restore path from this action.</span>
                        </div>
                        <ModalChecklist>
                          <li>Confirm no one on your team still needs this book.</li>
                          <li>Download any manuscripts or exports you might reuse.</li>
                        </ModalChecklist>
                      </section>
                    </div>
                  </ModalBody>
                </Modal>

                <Modal
                  title={
                    <div className="manage-books-modal-title-block">
                      <ModalTitle>Upload Book</ModalTitle>
                      <ModalSubtitle>Add a manuscript and the details needed to organize it.</ModalSubtitle>
                    </div>
                  }
                  visible={isUploadModalVisible}
                  onCancel={handleModalClose}
                  footer={null}
                  width={672}
                  centered
                  destroyOnClose
                  maskClosable={!tutorialModeActive}
                  className="manage-books-modal manage-books-upload-modal"
                  afterOpenChange={(open) => {
                    setUploadModalReady(open);
                    if (open) {
                      if (pendingUploadTourStep !== null) {
                        setUploadTourStep(pendingUploadTourStep);
                        setPendingUploadTourStep(null);
                      }
                    } else {
                      setPendingUploadTourStep(null);
                    }
                  }}
                >
                  <div ref={uploadModalRef} data-testid="tutorial:upload-book:modal-ready">
                    <BookUploadForm
                      key={formKey}
                      onUpload={handleUpload}
                      onAnalyze={handleNewBookPreflight}
                      onReset={handleModalClose}
                      authors={uniqueAuthors}
                      seriesList={uniqueSeries}
                      simpleMode={simpleModeEnabled}
                    />
                  </div>
                </Modal>

                <Modal
                  title={
                    <div className="manage-books-modal-title-block">
                      <ModalTitle>Replace Manuscript</ModalTitle>
                      <ModalSubtitle>
                        {replaceTargetBook ? replaceTargetBook.title || 'Untitled book' : 'Select a book to continue'}
                      </ModalSubtitle>
                    </div>
                  }
                  visible={isReplaceModalVisible}
                  onCancel={closeReplaceModal}
                  onOk={handleReplaceSubmit}
                  okText="Replace Manuscript"
                  okButtonProps={{ danger: true, disabled: !replaceFile || !replacePreviewToken || isReplacing || isReplacePreviewing || isCheckingReplaceLock || !!replaceError, loading: isReplacing }}
                  cancelButtonProps={{ disabled: isReplacing }}
                  destroyOnClose={false}
                  maskClosable={!isReplacing && !isReplacePreviewing}
                  width={760}
                  className="manage-books-modal manage-books-replace-modal"
                >
                  <div className="manage-books-modal-stack">
                    <div className="manage-books-modal-block manage-books-modal-guidance">
                      <div className="manage-books-modal-block-title">What changes</div>
                      <p>
                        Upload a .docx or .epub file to overwrite the existing chapters and replace the manuscript for this book.
                      </p>
                    </div>
                    <Alert
                      type="warning"
                      showIcon
                      message="Replacing a manuscript deletes all translated and proofread chapters (metadata translations remain untouched). This cannot be undone."
                    />
                    {isCheckingReplaceLock && (
                      <Alert
                        type="info"
                        showIcon
                        message="Checking if this book is busy..."
                      />
                    )}
                    <div className="manage-books-modal-block">
                      <div className="manage-books-modal-block-header">
                        <div>
                          <div className="manage-books-modal-block-title">Replacement file</div>
                          <p>Choose the manuscript that should become the new source for this book.</p>
                        </div>
                        <span className={`manage-books-readiness-pill ${replacePreviewToken ? 'is-ready' : replaceError ? 'is-blocked' : 'is-needed'}`}>
                          {replacePreviewToken ? 'Ready' : replaceError ? 'Attention' : 'Preview needed'}
                        </span>
                      </div>
                      <Upload.Dragger
                        multiple={false}
                        beforeUpload={handleReplaceBeforeUpload}
                        onRemove={handleReplaceRemove}
                        fileList={replaceUploadFileList}
                        accept=".docx,.epub"
                        disabled={isReplacing || isReplacePreviewing || isCheckingReplaceLock || !!replaceError}
                        name="replace-file"
                        showUploadList={false}
                        className="manage-books-replace-dropzone"
                      >
                        {replaceFile ? (
                          <div className="manage-books-selected-file">
                            <UploadOutlined />
                            <div className="manage-books-selected-file-main">
                              <div className="manage-books-selected-file-name">{replaceFile.name}</div>
                              <div className="manage-books-selected-file-size">
                                {(replaceFile.size / (1024 * 1024)).toFixed(2)} MB
                              </div>
                            </div>
                            <Button
                              type="link"
                              icon={<CloseOutlined />}
                              onClick={(e) => {
                                e.preventDefault();
                                resetReplaceSelection();
                              }}
                              disabled={isReplacing}
                            >
                              Choose a different file
                            </Button>
                          </div>
                        ) : (
                          <>
                            <p className="ant-upload-drag-icon">
                              <UploadOutlined />
                            </p>
                            <p className="ant-upload-text">
                              Click or drag a .docx or .epub file to replace the manuscript
                            </p>
                            <p className="ant-upload-hint">
                              All existing chapters will be replaced by the new upload.
                            </p>
                          </>
                        )}
                      </Upload.Dragger>
                    </div>
                    {isReplacePreviewing && (
                      <div className="manage-books-preview-loading">
                        <Spin tip="Generating preview..." />
                      </div>
                    )}
                    {replacePreviewError && (
                      <Alert type="error" message={replacePreviewError} showIcon />
                    )}
                    {replacePreviewRows.length > 0 && (
                      <div className="manage-books-modal-block">
                        <div className="manage-books-modal-block-header">
                          <div>
                            <div className="manage-books-modal-block-title">Detected Chapters</div>
                            <p>Review the parsed chapter list before replacing the manuscript.</p>
                          </div>
                          <span className="manage-books-readiness-pill is-ready">
                            {replacePreviewRows.length} chapters
                          </span>
                        </div>
                        <div className="manage-books-preview-list">
                          {replacePreviewRows.map(row => (
                            <div key={row.temp_id} className="manage-books-preview-row">
                              <div className="manage-books-preview-row-title">
                                <span>{row.title || `Chapter ${row.chapter_number}`}</span>
                                <span>
                                  {row.word_count.toLocaleString()} words
                                </span>
                              </div>
                              <div className="manage-books-preview-row-snippet">
                                {row.snippet
                                  ? `${row.snippet.slice(0, 140)}${row.snippet.length > 140 ? '…' : ''}`
                                  : 'No preview available.'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {replaceError && (
                      <Alert type="error" message={replaceError} showIcon />
                    )}
                  </div>
                </Modal>

                {!tutorialModeActive && (
                  <AppTour
                    open={uploadTourOpen}
                    steps={uploadTourSteps}
                    current={uploadTourStep}
                    onChange={handleUploadTourChange}
                    onClose={handleUploadTourClose}
                    onFinish={handleUploadTourClose}
                    zIndex={2000}
                  />
                )}
                
                <div className={`manage-books-results-scroll manage-books-results-scroll--${viewMode} manage-books-results-scroll--group-${groupByMode}`} data-testid="tutorial:manage-books:results">
                  {filteredGroups.length === 0 ? (
                    <EmptyState className="manage-books-state-panel">
                      {bookDetails.length === 0 ? (
                        <div className="manage-books-empty-library-card app-surface-card">
                          <BookOutlined className="manage-books-empty-library-icon" />
                          <h2>Upload your first book</h2>
                          <p>Add an EPUB, DOCX, or manuscript file to start translating with AI</p>
                        </div>
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_DEFAULT}
                          description="No books match your current filters."
                        />
                      )}
                      {bookDetails.length === 0 && (
                        <Button
                          type="primary"
                          className="app-primary-cta"
                          icon={<UploadOutlined />}
                          onClick={handleModalOpen}
                          size="large"
                          data-testid="tutorial:upload-book:open-upload-dialog-empty"
                        >
                          Upload Book
                        </Button>
                      )}
                      {bookDetails.length === 0 && (
                        <Button type="link" onClick={() => navigate('/support#support-articles')}>
                          Learn how book translation works
                        </Button>
                      )}
                      {bookDetails.length > 0 && (
                        <Button icon={<ClearOutlined />} onClick={clearFilters}>
                          Clear Filters
                        </Button>
                      )}
                    </EmptyState>
                  ) : (
                    viewMode === 'list' ? (
                      groupByMode === 'none'
                        ? renderBookList(flatVisibleBooks)
                        : groupByMode === 'author_series'
                        ? authorSeriesListContent
                        : groupedListContent
                    ) : (
                      groupByMode === 'none'
                        ? flatCoverContent
                        : groupByMode === 'author_series'
                        ? groupsContent
                        : groupedCoverContent
                    )
                  )}
                </div>
              </>
            ) : (
              <>
                {!isLoading && !user && (
                  <>
                    {tutorialModeActive ? (
                      <EmptyState className="manage-books-state-panel">
                        <LoadingCard label="Reconnecting your tutorial session..." />
                      </EmptyState>
                    ) : (
                      <EmptyState className="manage-books-state-panel">
                        <Empty
                          image={Empty.PRESENTED_IMAGE_DEFAULT}
                          description="Please click Get Started to log in and view your books."
                        />
                        <Button
                          type="primary"
                          className="app-primary-cta"
                          size="large"
                          onClick={() => showLoginModal && showLoginModal()}
                        >
                          Get Started
                        </Button>
                      </EmptyState>
                    )}
                  </>
                )}
              </>
            )}
            </>
          )}  
          </>
        )}      
      </StyledCard>
      </div>
    </Container>
  );
};

export default BookManagementTab;
